import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ReachWorldMap } from "@/components/partner/ReachWorldMap";
import { PartnerUpcomingCalendar } from "@/components/partner/PartnerUpcomingCalendar";
import { syncInterviewEvent } from "@/lib/partnerGoogleCalendar";
import {
  ArrowDownRight,
  ArrowUpRight,
  Badge,
  Bookmark,
  Briefcase,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  Download,
  Edit,
  Eye,
  ExternalLink,
  Filter,
  Flag,
  Globe,
  Heart,
  Image as ImageIcon,
  LocateFixed,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  Newspaper,
  Paperclip,
  Plus,
  Search,
  Send,
  Share2,
  Sparkles,
  Star,
  Tag,
  Trash2,
  TrendingUp,
  UploadCloud,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { detectUserLocation } from "@/lib/detectUserLocation";
import {
  dashboardMetrics,
} from "@/data/partnerPortalContent";
import { usePartnerData, PartnershipMessage, type PartnerFollower, type PartnerPost, type PostReactor, type PostReply } from "@/hooks/usePartnerData";
import {
  FREE_TRIAL_FEATURES,
  PRO_PLUS_FEATURES,
  planFeatureList,
} from "@/data/partnerBillingPlans";
import {
  getPartnerProfileMissingFields,
  isPartnerProfileComplete,
} from "@/lib/partnerProfileComplete";
import { supabase } from "@/lib/supabase";
import { joinPartnershipTyping, type PartnershipTypingSession } from "@/lib/partnershipTyping";
import { cn } from "@/lib/utils";
import { requirementLabel } from "@/data/applicationRequirementCatalog";

function VerificationGateBanner({
  isVerified,
  profileComplete,
  missing,
}: {
  isVerified: boolean;
  profileComplete: boolean;
  missing: string[];
}) {
  if (isVerified) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      {profileComplete ? (
        <p>
          Your public profile is complete. Skutopia admins will review and verify your
          organization before you can publish listings.
        </p>
      ) : (
        <p>
          Complete your{" "}
          <Link to="/partners/organization" className="font-semibold underline">
            organization profile
          </Link>{" "}
          (and save changes) before you can be verified and publish listings.
          {missing.length ? (
            <span className="mt-1 block text-amber-900">Still missing: {missing.join(", ")}</span>
          ) : null}
        </p>
      )}
    </div>
  );
}

function CreateListingButton({
  isVerified,
  className,
  children,
}: {
  isVerified: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const { programs, isProPlus, maxProgramListings } = usePartnerData();
  const atLimit = programs.length >= maxProgramListings;

  if (!isVerified) {
    return (
      <Button
        type="button"
        disabled
        title="Only verified partners can publish listings"
        className={cn("opacity-50 cursor-not-allowed", className)}
      >
        {children}
      </Button>
    );
  }

  if (atLimit && !isProPlus) {
    return (
      <Link to="/partners/billing">
        <Button
          type="button"
          title="Free Trial includes up to 3 program listings (5 listings total: 3 programs & 2 posts). Upgrade to Pro+ for unlimited listings."
          className={cn(className)}
          onClick={() =>
            toast.message("Upgrade to Pro+", {
              description: "Free Trial includes up to 3 program listings (5 total listings: 3 programs & 2 posts). Upgrade to Pro+ for unlimited listings.",
            })
          }
        >
          {children}
        </Button>
      </Link>
    );
  }

  if (atLimit) {
    return (
      <Button
        type="button"
        disabled
        title={`Your plan allows up to ${maxProgramListings} program listings.`}
        className={cn("opacity-50 cursor-not-allowed", className)}
      >
        {children}
      </Button>
    );
  }

  return (
    <Link to="/partners/programs/new">
      <Button className={className}>{children}</Button>
    </Link>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export const PortalCard = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "rounded-lg border border-[#d0d7de] bg-white",
      className
    )}
  >
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  index,
}: {
  label: string;
  value: string | number;
  index: number;
}) => (
  <motion.div variants={fadeUp} custom={index} initial="hidden" animate="visible">
    <div className="rounded-lg border border-[#d0d7de] bg-white p-4">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#656d76]">{label}</p>
      <p className="text-2xl font-semibold tracking-tight text-[#1f2328]">{value}</p>
    </div>
  </motion.div>
);

const statusStyles: Record<string, string> = {
  new: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  pending: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  reviewing: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  shortlisted: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  interview: "bg-skutopia-50 text-skutopia-700 dark:bg-skutopia-950/40 dark:text-skutopia-300",
  accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  rejected: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
};

const normalizeAppStatus = (status: string) =>
  status === "pending" ? "new" : status;

/** ISO timestamp → value usable by an <input type="datetime-local"> (local time, no seconds). */
const toDatetimeLocalValue = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* ─── DASHBOARD ─── */
export const PartnerDashboardView = () => {
  const { user } = useAuth();
  const { profile, programs, posts, analytics, applications, activities, messages, activeCount, loading } =
    usePartnerData();
  const isVerified = Boolean(profile?.is_verified);
  const profileComplete = isPartnerProfileComplete(profile);
  const missingProfileFields = getPartnerProfileMissingFields(profile);

  const totalViews = analytics.reduce((sum, a) => sum + (a.total_views || 0), 0);
  const totalApplications = applications.length;
  const totalBookmarks = analytics.reduce((sum, a) => sum + (a.total_bookmarks || 0), 0);
  const newApplications = applications.filter(
    (a) => normalizeAppStatus(a.status) === "new"
  );
  // Only incoming messages that haven't been opened yet (exclude ones we sent).
  const unreadMessages = messages.filter(
    (m) =>
      !m.is_read &&
      Boolean(user?.id) &&
      m.receiver_id === user!.id &&
      m.sender_id !== user!.id
  );
  const drafts = programs.filter((p) => !p.is_active);

    const now = new Date();
  const upcomingDeadlines = programs.filter((p) => {
      if (!p.application_deadline) return false;
      const deadline = new Date(p.application_deadline);
    const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntil > 0 && daysUntil <= 14;
  });

  const workItems: { title: string; href: string; urgency: "high" | "medium" | "low" }[] = [];
  if (newApplications.length > 0) {
    workItems.push({
      title: `${newApplications.length} application${newApplications.length === 1 ? "" : "s"} to review`,
      href: "/partners/applications",
      urgency: "high",
    });
  }
    if (upcomingDeadlines.length > 0) {
    const next = upcomingDeadlines
      .slice()
      .sort(
        (a, b) =>
          new Date(a.application_deadline!).getTime() - new Date(b.application_deadline!).getTime()
      )[0];
    const days = Math.ceil(
      (new Date(next.application_deadline!).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    workItems.push({
      title: `${next.title} closes in ${days} day${days === 1 ? "" : "s"}`,
      href: `/partners/programs/view/${next.id}`,
      urgency: "medium",
    });
  }
  if (unreadMessages.length > 0) {
    workItems.push({
      title: `${unreadMessages.length} unread message${unreadMessages.length === 1 ? "" : "s"}`,
      href: "/partners/messages",
      urgency: "medium",
    });
  }
  if (drafts.length > 0) {
    workItems.push({
      title: `${drafts.length} draft${drafts.length === 1 ? "" : "s"} ready to publish`,
      href: "/partners/programs",
      urgency: "low",
    });
  }

  const metricValues: Record<string, string | number> = {
    activeOpportunities: activeCount,
    applications: totalApplications,
    profileViews: totalViews,
    bookmarks: totalBookmarks,
  };

  const orgName = profile?.institution_name || "Your organization";
  const isEmpty = programs.length === 0 && applications.length === 0;

  return (
    <div className="space-y-6">
      <div className="border-b border-[#d0d7de] pb-5">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#656d76]">
          Recruiter home
        </p>
        <h2 className="text-xl font-semibold tracking-tight text-[#1f2328] sm:text-2xl">
          {orgName}
          </h2>
        <p className="mt-1 max-w-xl text-sm text-[#656d76]">
          Review new applications, reply to students, and publish listings that appear in Discover.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/partners/applications"
            className="rounded-md bg-[#00573C] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#004530]"
          >
            Review applications{newApplications.length ? ` (${newApplications.length})` : ""}
          </Link>
          {isVerified ? (
            <Link
              to="/partners/programs/new"
              className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-sm font-semibold text-[#1f2328] hover:bg-[#f6f8fa]"
            >
              Publish program
            </Link>
          ) : (
            <span
              title="Only verified partners can publish programs"
              className="cursor-not-allowed rounded-md border border-[#d0d7de] bg-[#f6f8fa] px-3 py-1.5 text-sm font-semibold text-[#8c959f]"
            >
              Publish program
            </span>
          )}
          <Link
            to="/partners/messages"
            className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-sm font-semibold text-[#1f2328] hover:bg-[#f6f8fa]"
          >
            Messages{unreadMessages.length ? ` (${unreadMessages.length})` : ""}
          </Link>
          <Link
            to="/partners/posts?action=create"
            className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-sm font-semibold text-[#1f2328] hover:bg-[#f6f8fa] inline-flex items-center gap-1.5"
          >
            <Newspaper className="w-4 h-4 text-[#00573C]" />
            New post
          </Link>
        </div>
      </div>

      <VerificationGateBanner
        isVerified={isVerified}
        profileComplete={profileComplete}
        missing={missingProfileFields}
      />

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="h-7 w-7 rounded-full border-2 border-skutopia-500 border-t-transparent animate-spin" />
            </div>
      ) : isEmpty ? (
        <PortalCard className="p-8 sm:p-10">
          <div className="max-w-lg">
            <div className="h-12 w-12 rounded-2xl bg-skutopia-50 flex items-center justify-center mb-5">
              <Briefcase className="w-6 h-6 text-skutopia-600" />
          </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Publish your first program</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Students browse Discover for scholarships, university programs, summer schools, gap-year
              options, and internships. Your listing should include grade range, funding, deadline, and
              a clear apply link.
            </p>
            <CreateListingButton
              isVerified={isVerified}
              className="bg-skutopia-500 hover:bg-skutopia-600 text-white rounded-xl font-semibold"
            >
              Create listing
            </CreateListingButton>
          </div>
        </PortalCard>
      ) : (
        <>
          {/* Primary work queue */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">Today</h3>
      </div>
            <PortalCard className="divide-y divide-gray-100">
              {workItems.length === 0 ? (
                <div className="p-5 text-sm text-gray-500">
                  Nothing waiting. Your live listings are reaching students on Discover.
                  </div>
              ) : (
                workItems.map((item) => (
                  <Link
                key={item.title}
                    to={item.href}
                    className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <span
                className={cn(
                        "h-2 w-2 rounded-full shrink-0",
                        item.urgency === "high" && "bg-red-500",
                        item.urgency === "medium" && "bg-amber-500",
                        item.urgency === "low" && "bg-skutopia-400"
                      )}
                    />
                    <span className="flex-1 text-sm font-semibold text-gray-900">{item.title}</span>
                    <span className="text-gray-300" aria-hidden>
                      →
                    </span>
                  </Link>
                ))
              )}
        </PortalCard>
          </section>

          <PartnerUpcomingCalendar />

          {/* Compact secondary metrics */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-gray-200/80 pt-6">
            {dashboardMetrics.map((m, i) => (
              <StatCard key={m.key} label={m.label} value={metricValues[m.key] ?? 0} index={i} />
            ))}
          </section>

          {/* Live listings */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                Live on Discover
              </h3>
              <Link
                to="/partners/programs"
                className="text-sm font-semibold text-skutopia-600 hover:text-skutopia-700"
              >
                View all
            </Link>
                  </div>
            <PortalCard className="divide-y divide-gray-100">
          {loading ? (
                <div className="py-10 flex justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-skutopia-500 border-t-transparent animate-spin" />
                </div>
          ) : programs.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">No listings yet.</div>
              ) : (
                programs.slice(0, 5).map((p) => (
                  <Link
                  key={p.id}
                    to={`/partners/programs/view/${p.id}`}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-[#f6f8fa] ring-1 ring-[#d0d7de]">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="text-xs font-bold text-[#00573C]">
                          {(p.title || "?").slice(0, 2).toUpperCase()}
                        </span>
            )}
          </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {[p.category, p.location || "Global", "All students"]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                  </div>
                    <span
                      className={cn(
                        "text-[11px] font-bold px-2 py-1 rounded-md",
                        p.is_active
                          ? "bg-skutopia-50 text-skutopia-700"
                          : "bg-gray-100 text-gray-500"
                      )}
                    >
                      {p.is_active ? "Live" : "Closed"}
                    </span>
                  </Link>
                ))
              )}
        </PortalCard>
          </section>

          {/* Campus Posts & Timeline Feed */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                  Campus Feed & Posts
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Posts shared directly to your mobile institution profile timeline and Discover
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/partners/posts?action=create"
                  className="text-xs font-semibold bg-[#00573C] text-white px-2.5 py-1 rounded hover:bg-[#004530] inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New post
                </Link>
                <Link
                  to="/partners/posts"
                  className="text-sm font-semibold text-skutopia-600 hover:text-skutopia-700"
                >
                  View all ({posts.length})
            </Link>
          </div>
            </div>
            <PortalCard className="divide-y divide-gray-100">
          {loading ? (
                <div className="py-10 flex justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-skutopia-500 border-t-transparent animate-spin" />
            </div>
              ) : posts.length === 0 ? (
                <div className="p-6 text-sm text-gray-500 flex flex-col items-center justify-center text-center gap-2">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                  <p>No campus stories or posts published yet.</p>
                  <Link
                    to="/partners/posts?action=create"
                    className="text-xs font-semibold text-[#00573C] hover:underline"
                  >
                    + Create your first post
              </Link>
            </div>
          ) : (
                posts.slice(0, 3).map((post) => (
                  <Link
                    key={post.id}
                    to="/partners/posts"
                    className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-[#f6f8fa] ring-1 ring-[#d0d7de]">
                      {post.media_image_url ? (
                        <img
                          src={post.media_image_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Newspaper className="w-5 h-5 text-[#00573C]" />
                      )}
                  </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {post.title || post.body.slice(0, 60)}
                        </p>
                        <span
                          className={cn(
                            "px-1.5 py-0.2 rounded text-[10px] font-semibold",
                            post.is_published
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-800"
                          )}
                        >
                          {post.is_published ? "Live" : "Draft"}
                        </span>
                </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {post.body}
                      </p>
            </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
                        {post.cheers_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3.5 h-3.5 text-blue-500" />
                        {post.reply_count || 0}
                      </span>
                    </div>
                  </Link>
                ))
          )}
        </PortalCard>
          </section>

          {activities.length > 0 && (
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">
                Recent activity
              </h3>
              <div className="space-y-3">
                {activities.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-skutopia-500 mt-2 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.action_type.replace(/_/g, " ")}
                      </p>
                      <p className="text-xs text-gray-500">{item.description}</p>
      </div>
                </div>
              ))}
            </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

/* ─── OPPORTUNITIES ─── */
export const PartnerOpportunitiesView = () => {
  const { programs, loading, deleteProgram, setProgramActive, profile } = usePartnerData();
  const isVerified = Boolean(profile?.is_verified);
  const profileComplete = isPartnerProfileComplete(profile);
  const missingProfileFields = getPartnerProfileMissingFields(profile);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    location: "",
    fundingType: "",
    dateRange: "all"
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleView = (programId: string) => {
    // Navigate to program detail view within the partner dashboard
    navigate(`/partners/programs/view/${programId}`);
  };

  const handleEdit = (programId: string) => {
    // Navigate to edit page with program ID
    navigate(`/partners/programs/edit/${programId}`);
  };

  // Search engine with multi-field matching
  const searchEngine = (items: any[], query: string, filters: any) => {
    if (!query && !filters.location && !filters.fundingType && filters.dateRange === "all") {
      return items;
    }

    return items.filter((item) => {
      // Text search across multiple fields
      if (query) {
        const searchLower = query.toLowerCase();
        const matchesTitle = item.title?.toLowerCase().includes(searchLower);
        const matchesDescription = item.description?.toLowerCase().includes(searchLower);
        const matchesLocation = item.location?.toLowerCase().includes(searchLower);
        const matchesType = item.opportunity_type?.toLowerCase().includes(searchLower);
        
        if (!matchesTitle && !matchesDescription && !matchesLocation && !matchesType) {
          return false;
        }
      }

      // Location filter
      if (filters.location && item.location?.toLowerCase() !== filters.location.toLowerCase()) {
        return false;
      }

      // Funding type filter
      if (filters.fundingType && item.funding_type?.toLowerCase() !== filters.fundingType.toLowerCase()) {
        return false;
      }

      // Date range filter
      if (filters.dateRange !== "all") {
        const itemDate = new Date(item.created_at);
        const now = new Date();
        
        if (filters.dateRange === "week") {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (itemDate < weekAgo) return false;
        } else if (filters.dateRange === "month") {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (itemDate < monthAgo) return false;
        } else if (filters.dateRange === "year") {
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          if (itemDate < yearAgo) return false;
        }
      }

      return true;
    });
  };

  // Apply status filter first, then search engine
  const statusFiltered =
    filter === "active"
      ? programs.filter((p) => p.is_active)
      : filter === "draft"
        ? programs.filter((p) => !p.is_active)
        : programs;

  const filtered = searchEngine(statusFiltered, searchQuery, searchFilters);

  const handleDelete = (id: string) => {
    setProgramToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!programToDelete || deleting) return;
    setDeleting(true);
    try {
      const { error } = await deleteProgram(programToDelete);
      if (error) {
        console.error("Delete program failed:", error);
        toast.error(error.message || "Could not delete this program.");
        return;
      }
      toast.success("Program deleted.");
      setDeleteDialogOpen(false);
      setProgramToDelete(null);
    } catch (e: unknown) {
      console.error("Delete program failed:", e);
      toast.error("Could not delete this program.");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setProgramToDelete(null);
  };

  const handleToggleActive = async (programId: string, nextActive: boolean) => {
    if (togglingId) return;
    setTogglingId(programId);
    try {
      const { error } = await setProgramActive(programId, nextActive);
      if (error) {
        toast.error(error.message || "Could not update this listing.");
        return;
      }
      toast.success(
        nextActive
          ? "Program published — visible on Discover again."
          : "Program closed — hidden from Discover. Existing applicants keep their history."
      );
    } finally {
      setTogglingId(null);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchFilters({ location: "", fundingType: "", dateRange: "all" });
  };

  const hasActiveFilters = searchQuery || searchFilters.location || searchFilters.fundingType || searchFilters.dateRange !== "all";

  const publishedCount = programs.filter((p) => p.is_active).length;
  const draftCount = programs.filter((p) => !p.is_active).length;

  const shortDate = (value?: string) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-[#656d76] max-w-xl">
          Manage programs students see in Discover. Publish only after your organization is verified.
        </p>
        <CreateListingButton
          isVerified={isVerified}
          className="rounded-md bg-[#00573C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004530] gap-2"
        >
            <Plus className="w-4 h-4" />
          New program
        </CreateListingButton>
      </div>

      <VerificationGateBanner
        isVerified={isVerified}
        profileComplete={profileComplete}
        missing={missingProfileFields}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Programs", value: programs.length },
          { label: "Published", value: publishedCount },
          { label: "Closed", value: draftCount },
          { label: "Showing", value: filtered.length },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-lg border border-[#d0d7de] bg-white px-4 py-3"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#656d76]">
              {stat.label}
            </p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-[#1f2328]">
              {loading ? "—" : stat.value}
            </p>
          </div>
        ))}
      </div>

      <PortalCard className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-[#d0d7de] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-[#1f2328]">All programs</h3>
          <div className="relative w-full sm:w-56">
            <Input
              type="text"
              placeholder="Search programs…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 rounded-md border-[#d0d7de]"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#656d76]" />
          </div>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex flex-wrap gap-2">
            {(
              [
                { id: "all", label: "All" },
                { id: "active", label: "Published" },
                { id: "draft", label: "Closed" },
              ] as const
            ).map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => setFilter(chip.id)}
                className={
                  filter === chip.id
                    ? "rounded-md bg-[#00573C] px-3 py-1 text-xs font-semibold text-white"
                    : "rounded-md border border-[#d0d7de] bg-white px-3 py-1 text-xs font-semibold text-[#1f2328] hover:bg-[#f6f8fa]"
                }
              >
                {chip.label}
                {chip.id === "active" && publishedCount ? ` (${publishedCount})` : ""}
                {chip.id === "draft" && draftCount ? ` (${draftCount})` : ""}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={searchFilters.location}
              onChange={(e) => setSearchFilters({ ...searchFilters, location: e.target.value })}
              className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-xs font-medium text-[#1f2328]"
            >
              <option value="">All locations</option>
              <option value="remote">Remote</option>
              <option value="on-site">On-site</option>
              <option value="hybrid">Hybrid</option>
            </select>
            <select
              value={searchFilters.fundingType}
              onChange={(e) => setSearchFilters({ ...searchFilters, fundingType: e.target.value })}
              className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-xs font-medium text-[#1f2328]"
            >
              <option value="">All funding</option>
              <option value="fullyFunded">Fully funded</option>
              <option value="partial">Partial</option>
              <option value="none">Self-funded</option>
            </select>
            <select
              value={searchFilters.dateRange}
              onChange={(e) => setSearchFilters({ ...searchFilters, dateRange: e.target.value })}
              className="rounded-md border border-[#d0d7de] bg-white px-3 py-1.5 text-xs font-medium text-[#1f2328]"
            >
              <option value="all">All time</option>
              <option value="week">Past week</option>
              <option value="month">Past month</option>
              <option value="year">Past year</option>
            </select>
            {hasActiveFilters && (
          <button
            type="button"
                onClick={clearSearch}
                className="rounded-md border border-[#d0d7de] px-3 py-1.5 text-xs font-semibold text-[#424a53] hover:bg-[#f6f8fa]"
              >
                Clear filters
          </button>
            )}
      </div>

        {loading ? (
          <div className="py-16 flex justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-[#00573C] border-t-transparent animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#d0d7de] bg-[#f6f8fa] px-6 py-14 text-center">
              <Briefcase className="w-10 h-10 text-[#00573C]/60 mx-auto mb-3" />
              <h4 className="font-semibold text-[#1f2328] mb-1">
                {hasActiveFilters ? "No programs match" : "No programs yet"}
              </h4>
              <p className="text-sm text-[#656d76] mb-5 max-w-md mx-auto">
                {hasActiveFilters
                  ? "Try another search or clear filters."
                  : "Publish a scholarship, summer program, internship, or placement for high school students."}
              </p>
              {!hasActiveFilters && (
                <CreateListingButton
                  isVerified={isVerified}
                  className="rounded-md bg-[#00573C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004530]"
                >
                  Publish program
                </CreateListingButton>
              )}
          </div>
        ) : (
            <div className="grid gap-3 md:grid-cols-2">
            {filtered.map((program) => (
                <article
                key={program.id}
                  className="flex gap-4 rounded-lg border border-[#d0d7de] bg-white p-4"
                >
                  <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#f6f8fa] ring-1 ring-[#d0d7de]">
                    {program.image_url ? (
                      <img
                        src={program.image_url}
                        alt={program.title || "Program"}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = "none";
                          const fallback = el.nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <span
                      className={cn(
                        "text-xs font-bold text-[#00573C]",
                        program.image_url ? "hidden" : ""
                      )}
                    >
                      {(program.title || "?").slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-semibold text-[#1f2328]">
                        {program.title || "Untitled program"}
                      </h3>
                      {program.is_active ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                          Published
                        </span>
                      ) : (
                        <span className="rounded-full bg-[#f6f8fa] px-2 py-0.5 text-[10px] font-semibold text-[#656d76] ring-1 ring-[#d0d7de]">
                          Closed
                        </span>
                      )}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-[#656d76]">
                      {program.description || "No description yet."}
                    </p>
                    <p className="mt-1 text-xs text-[#656d76]">
                      {[program.location, program.funding_type, `created ${shortDate(program.created_at)}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleView(program.id)}
                        className="rounded-md border border-[#d0d7de] px-2 py-1 text-xs font-semibold hover:bg-[#f6f8fa]"
                    >
                        View
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEdit(program.id)}
                        className="rounded-md border border-[#d0d7de] px-2 py-1 text-xs font-semibold hover:bg-[#f6f8fa]"
                    >
                        Edit
                    </button>
                    <button
                      type="button"
                      disabled={togglingId === program.id}
                      onClick={() => handleToggleActive(program.id, !program.is_active)}
                      className="rounded-md border border-[#d0d7de] px-2 py-1 text-xs font-semibold hover:bg-[#f6f8fa] disabled:opacity-50"
                      title={
                        program.is_active
                          ? "Hide from Discover — applicants keep their history"
                          : "Make visible on Discover again"
                      }
                    >
                        {togglingId === program.id
                          ? "Updating…"
                          : program.is_active
                            ? "Close"
                            : "Publish"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(program.id)}
                        className="rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    >
                        Delete
                    </button>
                  </div>
                </div>
                </article>
            ))}
          </div>
        )}
        </div>
      </PortalCard>

      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-[#d0d7de] shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-[#1f2328] mb-2">Delete program</h3>
            <p className="text-sm text-[#656d76] mb-2">
              This permanently removes the program, its analytics, and any applications tied to it. This cannot be undone.
            </p>
            <p className="text-sm text-[#656d76] mb-6">
              Just want to stop taking new applicants? Use <strong>Close</strong> instead — it keeps existing applicants' history intact and you can publish again later.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={deleting}
                className="rounded-md"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="rounded-md bg-rose-700 hover:bg-rose-800 text-white"
              >
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const APP_STATUS_FILTERS = [
  "all",
  "new",
  "reviewing",
  "shortlisted",
  "interview",
  "accepted",
  "rejected",
] as const;

const APP_STATUS_OPTIONS = [
  "new",
  "reviewing",
  "shortlisted",
  "interview",
  "accepted",
  "rejected",
] as const;

type PartnerAppRow = ReturnType<typeof usePartnerData>["applications"][number];

type ApplicantDoc = {
  id?: string;
  name?: string;
  category?: string;
  sub_category?: string;
  bucket?: string;
  remote_path?: string;
  mime_type?: string;
  source?: string;
};

type ApplicantDossier = {
  application_id?: string;
  student_id?: string;
  personal_details?: Record<string, unknown> | null;
  academic_details?: Record<string, unknown> | null;
  admissions_profile?: Record<string, unknown> | null;
  documents?: ApplicantDoc[];
};

const DocLinks = ({
  docs,
  openingDoc,
  onOpen,
}: {
  docs: ApplicantDoc[];
  openingDoc: string | null;
  onOpen: (doc: ApplicantDoc) => void;
}) => {
  if (!docs.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {docs.map((d, idx) => (
        <button
          key={`${d.remote_path || d.id || idx}-doc`}
          type="button"
          className="inline-flex items-center gap-1 text-xs text-skutopia-600 underline disabled:opacity-50"
          disabled={!d.remote_path || openingDoc === d.remote_path}
          onClick={() => onOpen(d)}
        >
          <Paperclip className="w-3 h-3" />
          {openingDoc === d.remote_path ? "Opening..." : d.name || "Document"}
        </button>
      ))}
    </div>
  );
};

const ApplicantAvatar = ({
  name,
  src,
  className,
}: {
  name?: string;
  src?: string | null;
  className?: string;
}) => {
  const [failed, setFailed] = useState(false);
  const initial = (name?.trim().charAt(0) || "U").toUpperCase();
  const showImage = Boolean(src) && !failed;

  return (
    <div
      className={cn(
        "rounded-full bg-gradient-to-br from-skutopia-500 to-skutopia-700 flex items-center justify-center text-white font-medium overflow-hidden shrink-0",
        className
      )}
    >
      {showImage ? (
        <img
          src={src!}
          alt={name || "Applicant"}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initial
      )}
    </div>
  );
};

const PartnerFollowersPanel = ({
  followers,
}: {
  followers: PartnerFollower[];
}) => {
  const { isProPlus, messages, user } = usePartnerData();
  const navigate = useNavigate();

  const openFollowerChat = (follower: PartnerFollower) => {
    if (!isProPlus) {
      toast.message("Pro+ feature", {
        description: "Upgrade to Pro+ to message students who follow your institution.",
        action: {
          label: "Upgrade",
          onClick: () => navigate("/partners/billing"),
        },
      });
      return;
    }

    const existing = messages.find((m) => {
      const studentId = m.sender_id === user?.id ? m.receiver_id : m.sender_id;
      return studentId === follower.student_id;
    });

    const params = new URLSearchParams({
      with: follower.student_id,
      name: follower.display_name,
    });
    if (existing?.id) params.set("thread", existing.id);
    navigate(`/partners/messages?${params.toString()}`);
  };

  return (
  <PortalCard className="p-6">
    <h3 className="font-semibold mb-1">Student followers</h3>
    <p className="text-sm text-gray-500 mb-4">
      Learners who follow your institution in the Skutopia mobile app.
      {isProPlus
        ? " You can message them directly with Pro+."
        : " Direct contact is available on Pro+."}
    </p>
    {followers.length === 0 ? (
      <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-8 text-center">
        <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No followers yet.</p>
        <p className="text-xs text-gray-400 mt-1">
          Students can follow you from Social → Institutions after you complete your organization profile.
        </p>
      </div>
    ) : (
      <div className="space-y-3">
        {followers.map((follower) => (
          <div
            key={follower.student_id}
            className="flex items-center justify-between gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="flex items-center gap-3 min-w-0">
              <ApplicantAvatar
                name={follower.display_name}
                src={follower.profile_image_url || undefined}
                className="h-10 w-10 text-sm shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{follower.display_name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {[follower.country, follower.school, follower.grade ? `Grade ${follower.grade}` : null]
                    .filter(Boolean)
                    .join(" · ") || "Profile details not set"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <p className="text-xs text-gray-400 hidden sm:block">
                {new Date(follower.followed_at).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
              <Button
                type="button"
                size="sm"
                variant={isProPlus ? "default" : "outline"}
                className={cn(
                  "rounded-xl gap-1.5",
                  isProPlus && "bg-skutopia-600 hover:bg-skutopia-700 text-white"
                )}
                onClick={() => openFollowerChat(follower)}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {isProPlus ? "Contact" : "Pro+"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </PortalCard>
  );
};

/* ─── APPLICATIONS ─── */
export const PartnerApplicationsView = () => {
  const {
    applications,
    updateApplicationStatus,
    deleteApplication,
    sendApplicationMessage,
    refresh,
  } = usePartnerData();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [applicantSearch, setApplicantSearch] = useState("");
  const [openingDoc, setOpeningDoc] = useState<string | null>(null);
  const [reviewApp, setReviewApp] = useState<PartnerAppRow | null>(null);
  const [interviewMeetLink, setInterviewMeetLink] = useState<string | null>(null);
  const [interviewForm, setInterviewForm] = useState({
    eventStart: "",
    meetLink: "",
    notes: "",
  });
  const [interviewSaving, setInterviewSaving] = useState(false);
  const [pendingRejection, setPendingRejection] = useState(false);
  const [rejectionReasonDraft, setRejectionReasonDraft] = useState("");

  const [statusSaving, setStatusSaving] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [reviewDossier, setReviewDossier] = useState<ApplicantDossier | null>(null);
  const [dossierLoading, setDossierLoading] = useState(false);

  const openAttachedDocument = async (doc: {
    name?: string;
    bucket?: string;
    remote_path?: string;
  }) => {
    if (!doc.remote_path) {
      toast.error("This document has no cloud path yet.");
      return;
    }
    const key = doc.remote_path;
    setOpeningDoc(key);
    try {
      const bucket = doc.bucket || "student-documents";
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(doc.remote_path, 60 * 10);
      if (error || !data?.signedUrl) {
        const detail =
          error && typeof error === "object" && "message" in error
            ? String((error as { message?: string }).message || "")
            : "";
        throw new Error(
          detail
            ? `Could not open document: ${detail}`
            : "Could not create a signed link for this document."
        );
      }
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Could not open document.";
      toast.error(message);
    } finally {
      setOpeningDoc(null);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: applications.length };
    for (const s of APP_STATUS_FILTERS) {
      if (s === "all") continue;
      counts[s] = applications.filter((a) => normalizeAppStatus(a.status) === s).length;
    }
    return counts;
  }, [applications]);
  
  const filtered = useMemo(() => {
    const q = applicantSearch.trim().toLowerCase();
    return applications.filter((a) => {
      if (statusFilter !== "all" && normalizeAppStatus(a.status) !== statusFilter) {
        return false;
      }
      if (!q) return true;
      const hay = [
        a.student_name,
        a.student_email,
        a.program_title,
        a.student_school,
        a.student_grade,
        a.country,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [applications, statusFilter, applicantSearch]);

  const loadInterviewDetails = async (applicationId: string) => {
    const { data: cal } = await supabase
      .from("partner_calendar_events")
      .select("event_start, meet_link, notes")
      .eq("application_id", applicationId)
      .eq("event_type", "interview")
      .maybeSingle();
    setInterviewMeetLink(cal?.meet_link || null);
    setInterviewForm({
      eventStart: toDatetimeLocalValue(cal?.event_start),
      meetLink: cal?.meet_link || "",
      notes: cal?.notes || "",
    });
  };

  const openReview = async (app: PartnerAppRow) => {
    setReviewApp(app);
    setInterviewMeetLink(null);
    setInterviewForm({ eventStart: "", meetLink: "", notes: "" });
    setMessageDraft("");
    setReviewDossier(null);
    setDossierLoading(true);
    setPendingRejection(false);
    setRejectionReasonDraft(app.rejection_reason || "");
    // Opening clears the Applications nav badge (pending/new → reviewing).
    if (normalizeAppStatus(app.status) === "new") {
      const { error } = await updateApplicationStatus(app.id, "reviewing");
      if (!error) {
        setReviewApp({ ...app, status: "reviewing" });
      }
    }
    try {
      const { data, error } = await supabase.rpc("get_partner_applicant_dossier", {
        p_application_id: app.id,
      });
      if (error) throw error;
      setReviewDossier((data || null) as ApplicantDossier | null);
    } catch (e: unknown) {
      console.error("Applicant dossier failed:", e);
      toast.error("Could not load full Skutopian profile. Showing application snapshot.");
      setReviewDossier(null);
    } finally {
      setDossierLoading(false);
    }

    if (normalizeAppStatus(app.status) === "interview") {
      await loadInterviewDetails(app.id);
    }
  };

  const changeStatus = async (status: string, rejectionReason?: string) => {
    if (!reviewApp) return;
    setStatusSaving(true);
    const { error } = await updateApplicationStatus(reviewApp.id, status, rejectionReason);
    setStatusSaving(false);
    if (error) {
      toast.error("Could not update status.");
      return;
    }
    toast.success(`Status updated to ${status}`);
    setReviewApp({
      ...reviewApp,
      status: status === "pending" ? "new" : status,
      rejection_reason: status === "rejected" ? rejectionReason || null : null,
    });
    if (status === "interview") {
      await loadInterviewDetails(reviewApp.id);
    } else {
      setInterviewMeetLink(null);
      setInterviewForm({ eventStart: "", meetLink: "", notes: "" });
    }
    await refresh();
  };

  const saveInterviewDetails = async () => {
    if (!reviewApp) return;
    if (!interviewForm.eventStart) {
      toast.error("Pick an interview date and time first.");
      return;
    }
    setInterviewSaving(true);
    try {
      const { error } = await supabase.rpc("schedule_application_interview", {
        p_application_id: reviewApp.id,
        p_event_start: new Date(interviewForm.eventStart).toISOString(),
        p_meet_link: interviewForm.meetLink.trim() || null,
        p_notes: interviewForm.notes.trim() || null,
      });
      if (error) throw error;
      setInterviewMeetLink(interviewForm.meetLink.trim() || null);
      // Push the partner-picked slot to Google Calendar when connected
      // (edge function prefers this event_start over any placeholder).
      try {
        await syncInterviewEvent(reviewApp.id);
      } catch {
        /* calendar optional */
      }
      toast.success("Interview details saved — the student has been notified.");
      await refresh();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Could not save interview details.");
    } finally {
      setInterviewSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!reviewApp || !messageDraft.trim()) return;
    setMessageSending(true);
    const { error } = await sendApplicationMessage({
      studentId: reviewApp.student_id,
      applicationId: reviewApp.id,
      subject: `Re: ${reviewApp.program_title || "your application"}`,
      content: messageDraft.trim(),
    });
    setMessageSending(false);
    if (error) {
      toast.error("Could not send message.");
      return;
    }
    toast.success("Message sent to student");
    setMessageDraft("");
    navigate("/partners/messages");
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Country", "School", "Grade", "Program", "Status", "Applied at"],
      ...filtered.map((app) => [
        app.student_name || "",
        app.student_email || "",
        app.country || "",
        app.student_school || "",
        app.student_grade || "",
        app.program_title || "",
        normalizeAppStatus(app.status),
        app.applied_at || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "applications.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const appResponses = (app: PartnerAppRow) =>
    (app.application_responses || {}) as Record<string, unknown>;

  const isIncompleteApp = (app: PartnerAppRow) =>
    Boolean(appResponses(app).incomplete);

  const checklistItems = (app: PartnerAppRow) => {
    const raw = appResponses(app).requirements_checklist;
    if (!Array.isArray(raw)) return [] as Array<{ key?: string; label?: string; met?: boolean }>;
    return raw as Array<{ key?: string; label?: string; met?: boolean }>;
  };

  const personalDetails = (app: PartnerAppRow) => {
    if (reviewDossier?.personal_details && typeof reviewDossier.personal_details === "object") {
      return reviewDossier.personal_details;
    }
    const raw = appResponses(app).personal_details;
    if (raw && typeof raw === "object") return raw as Record<string, unknown>;
    // Legacy applications only snapshotted a thin academic_details block.
    const academic = appResponses(app).academic_details;
    if (academic && typeof academic === "object") {
      const a = academic as Record<string, unknown>;
      return { name: a.name, email: a.email };
    }
    return null;
  };

  const academicDetails = (app: PartnerAppRow) => {
    if (reviewDossier?.academic_details && typeof reviewDossier.academic_details === "object") {
      return reviewDossier.academic_details;
    }
    const raw = appResponses(app).academic_details;
    if (!raw || typeof raw !== "object") return null;
    return raw as Record<string, unknown>;
  };

  const admissionsProfile = (app: PartnerAppRow) => {
    if (reviewDossier?.admissions_profile && typeof reviewDossier.admissions_profile === "object") {
      return reviewDossier.admissions_profile;
    }
    const raw = appResponses(app).admissions_profile;
    if (!raw || typeof raw !== "object") return null;
    return raw as Record<string, unknown>;
  };

  const reviewDocuments = (app: PartnerAppRow): ApplicantDoc[] => {
    const fromDossier = Array.isArray(reviewDossier?.documents)
      ? reviewDossier!.documents!
      : [];
    if (fromDossier.length > 0) return fromDossier;
    return (app.submitted_documents || []) as ApplicantDoc[];
  };

  const asDocs = (value: unknown): ApplicantDoc[] =>
    Array.isArray(value)
      ? value.filter(
          (x): x is ApplicantDoc =>
            Boolean(x) && typeof x === "object" && Boolean((x as ApplicantDoc).remote_path)
        )
      : [];

  /** Legacy free-text responses (pre–Admissions Profile). */
  const legacyResponseItems = (app: PartnerAppRow) => {
    const responses = appResponses(app) as {
      requirements?: Array<{ text?: string }>;
    };
    return Array.isArray(responses.requirements) ? responses.requirements : [];
  };

  const asList = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value)
      ? value.filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === "object")
      : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#656d76]" />
          <Input
            value={applicantSearch}
            onChange={(e) => setApplicantSearch(e.target.value)}
            placeholder="Search name, email, school, program…"
            className="rounded-md border-[#d0d7de] bg-white pl-9"
          />
        </div>
        <Button
          variant="outline"
          className="gap-2 rounded-md border-[#d0d7de]"
          onClick={exportCsv}
        >
          <Download className="h-4 w-4" />
            Export CSV
          </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {APP_STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-semibold capitalize",
              statusFilter === s
                ? "border-[#00573C] bg-[#eff2f5] text-[#1f2328]"
                : "border-[#d0d7de] bg-white text-[#656d76] hover:bg-[#f6f8fa]",
            )}
          >
            {s}
            <span className="ml-1 opacity-70">({statusCounts[s] || 0})</span>
          </button>
        ))}
      </div>

      <PortalCard className="overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No applications in this status yet.
          </div>
        ) : (
          <>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 dark:bg-gray-800/50 text-left">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Applicant</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Program</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                          <ApplicantAvatar
                            name={app.student_name}
                            src={app.student_avatar_url}
                            className="h-9 w-9 text-sm"
                          />
                      <div>
                            <p className="text-sm font-medium">{app.student_name || "Unknown Student"}</p>
                            <p className="text-xs text-gray-500">{app.student_email || "No email"}</p>
                      </div>
                    </div>
                  </td>
                      <td className="px-6 py-4 text-xs text-gray-600">
                        <div>{app.country || "Country not set"}</div>
                        <div className="text-gray-400">
                          {[app.student_school, app.student_grade ? `Grade ${app.student_grade}` : null]
                            .filter(Boolean)
                            .join(" · ") || "School not set"}
                        </div>
                  </td>
                      <td className="px-6 py-4 text-sm max-w-[200px] truncate">{app.program_title || "Unknown Program"}</td>
                  <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5 items-start">
                          {isIncompleteApp(app) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                              <Flag className="w-3 h-3" />
                              Incomplete
                      </span>
                          )}
                          <select
                            className={cn(
                              "text-xs font-medium px-2.5 py-1.5 rounded-full capitalize border-0 cursor-pointer",
                              statusStyles[normalizeAppStatus(app.status)] || statusStyles.new
                            )}
                            value={normalizeAppStatus(app.status)}
                            onChange={async (e) => {
                              const { error } = await updateApplicationStatus(app.id, e.target.value);
                              if (error) toast.error("Could not update status.");
                              else toast.success("Status updated");
                            }}
                          >
                            {APP_STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-skutopia-600"
                            onClick={() => openReview(app)}
                          >
                        <Eye className="w-4 h-4 mr-1" /> Review
                      </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openReview(app)}
                            title="Message student"
                          >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            title="Delete application"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `Delete application from ${app.student_name || "this student"}? This cannot be undone.`
                                )
                              ) {
                                return;
                              }
                              const { error } = await deleteApplication(app.id);
                              if (error) toast.error("Could not delete application.");
                              else {
                                toast.success("Application deleted");
                                if (reviewApp?.id === app.id) setReviewApp(null);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">
          {filtered.map((app) => (
            <div key={app.id} className="p-4">
              <div className="flex items-start gap-3 mb-3">
                    <ApplicantAvatar
                      name={app.student_name}
                      src={app.student_avatar_url}
                      className="h-10 w-10"
                    />
                <div className="flex-1">
                      <p className="font-medium">{app.student_name || "Unknown Student"}</p>
                      <p className="text-xs text-gray-500">{app.student_email || "No email"}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {[app.country, app.student_school].filter(Boolean).join(" · ")}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{app.program_title || "Unknown Program"}</p>
                      {isIncompleteApp(app) && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          <Flag className="w-3 h-3" />
                          Incomplete
                </span>
                      )}
                    </div>
              </div>
              <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-xl"
                      onClick={() => openReview(app)}
                    >
                  Review
                </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => openReview(app)}
                    >
                  <MessageSquare className="w-4 h-4" />
                </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl text-red-600 border-red-200"
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `Delete application from ${app.student_name || "this student"}? This cannot be undone.`
                          )
                        ) {
                          return;
                        }
                        const { error } = await deleteApplication(app.id);
                        if (error) toast.error("Could not delete application.");
                        else {
                          toast.success("Application deleted");
                          if (reviewApp?.id === app.id) setReviewApp(null);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
          </>
        )}
      </PortalCard>

      {reviewApp && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <ApplicantAvatar
                  name={reviewApp.student_name}
                  src={reviewApp.student_avatar_url}
                  className="h-11 w-11 text-base"
                />
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {reviewApp.student_name}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">{reviewApp.program_title}</p>
    </div>
              </div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100"
                onClick={() => {
                  setReviewApp(null);
                  setInterviewMeetLink(null);
                  setReviewDossier(null);
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {isIncompleteApp(reviewApp) && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                  <Flag className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>
                    Flagged incomplete — one or more required Admissions Profile
                    components were missing at submit time.
                  </p>
                </div>
              )}

              {dossierLoading && (
                <p className="text-sm text-gray-500">Loading Skutopian profile…</p>
              )}

              {(() => {
                const personal = personalDetails(reviewApp);
                const display = (v: unknown) => {
                  const s = v == null ? "" : String(v).trim();
                  return s || "—";
                };
                const rows: Array<[string, string]> = [
                  ["Name", display(personal?.name ?? reviewApp.student_name)],
                  ["Email", display(personal?.email ?? reviewApp.student_email)],
                  ["Phone", display(personal?.phone)],
                  ["Date of birth", display(personal?.date_of_birth)],
                  ["Age", display(personal?.age)],
                  ["Gender", display(personal?.gender)],
                ];
                return (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                      Personal details
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm bg-gray-50 rounded-xl p-3">
                      {rows.map(([label, value]) => (
                        <div key={label}>
                          <p className="text-[11px] text-gray-400">{label}</p>
                          <p
                            className={
                              value === "—"
                                ? "font-medium text-gray-400"
                                : "font-medium"
                            }
                          >
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const academic = academicDetails(reviewApp);
                const display = (v: unknown) => {
                  const s = v == null ? "" : String(v).trim();
                  return s || "—";
                };
                const studyHours =
                  academic?.study_hours != null && Number(academic.study_hours) > 0
                    ? `${academic.study_hours} hrs`
                    : "—";
                const rows: Array<[string, string]> = [
                  ["School", display(academic?.school ?? reviewApp.student_school)],
                  ["Grade", display(academic?.grade ?? reviewApp.student_grade)],
                  ["Country", display(academic?.country ?? reviewApp.country)],
                  ["Exam board", display(academic?.exam_board)],
                  ["Bio", display(academic?.bio)],
                  ["Career plans", display(academic?.career_plans)],
                  ["Intended major", display(academic?.intended_major)],
                  ["Hobbies", display(academic?.hobbies)],
                  ["Preferred study", display(academic?.preferred_study_country)],
                  ["Study hours", studyHours],
                ];
                return (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                      Academic details
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm bg-gray-50 rounded-xl p-3">
                      {rows.map(([label, value]) => (
                        <div
                          key={label}
                          className={
                            ["Bio", "Career plans", "Hobbies"].includes(label)
                              ? "sm:col-span-2"
                              : undefined
                          }
                        >
                          <p className="text-[11px] text-gray-400">{label}</p>
                          <p
                            className={
                              value === "—"
                                ? "font-medium text-gray-400 whitespace-pre-wrap"
                                : "font-medium whitespace-pre-wrap"
                            }
                          >
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="text-xs uppercase tracking-wide text-gray-400 mb-2 block">Status</label>
                <select
                  disabled={statusSaving}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm capitalize"
                  value={normalizeAppStatus(reviewApp.status)}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === "rejected") {
                      setRejectionReasonDraft(reviewApp.rejection_reason || "");
                      setPendingRejection(true);
                      return;
                    }
                    setPendingRejection(false);
                    void changeStatus(next);
                  }}
                >
                  {APP_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {normalizeAppStatus(reviewApp.status) === "interview" ? (
                  <div className="mt-3 space-y-2 rounded-xl border border-skutopia-100 bg-skutopia-50/60 p-3">
                    <p className="text-xs font-semibold text-skutopia-800">
                      Interview details (shown to the student)
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-[11px] text-gray-500">Date &amp; time</span>
                        <input
                          type="datetime-local"
                          value={interviewForm.eventStart}
                          onChange={(e) =>
                            setInterviewForm((prev) => ({ ...prev, eventStart: e.target.value }))
                          }
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        />
                      </label>
                      <label className="block">
                        <span className="text-[11px] text-gray-500">Meet / call link (optional)</span>
                        <input
                          type="url"
                          placeholder="https://meet.google.com/..."
                          value={interviewForm.meetLink}
                          onChange={(e) =>
                            setInterviewForm((prev) => ({ ...prev, meetLink: e.target.value }))
                          }
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                        />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-[11px] text-gray-500">Notes for the student (optional)</span>
                      <textarea
                        rows={2}
                        placeholder="e.g. Please join 5 minutes early and have your ID ready."
                        value={interviewForm.notes}
                        onChange={(e) =>
                          setInterviewForm((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                      />
                    </label>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        size="sm"
                        disabled={interviewSaving}
                        onClick={saveInterviewDetails}
                        className="bg-skutopia-600 text-white hover:bg-skutopia-700"
                      >
                        {interviewSaving ? "Saving…" : "Save & notify student"}
                      </Button>
                      {interviewMeetLink ? (
                        <a
                          href={interviewMeetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-skutopia-600 hover:text-skutopia-700"
                        >
                          Open link
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {pendingRejection ? (
                  <div className="mt-3 space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
                    <label className="block text-xs font-semibold text-red-700">
                      Reason for rejection (shared with the student)
                    </label>
                    <textarea
                      value={rejectionReasonDraft}
                      onChange={(e) => setRejectionReasonDraft(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm"
                      placeholder="e.g. We received strong applicants this cycle and are unable to move forward with your application."
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingRejection(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={statusSaving}
                        className="bg-red-600 text-white hover:bg-red-700"
                        onClick={async () => {
                          await changeStatus("rejected", rejectionReasonDraft.trim() || undefined);
                          setPendingRejection(false);
                        }}
                      >
                        Confirm rejection
                      </Button>
                    </div>
                  </div>
                ) : reviewApp.rejection_reason ? (
                  <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    <span className="font-semibold text-gray-700">Reason shared with student: </span>
                    {reviewApp.rejection_reason}
                  </p>
                ) : null}
              </div>

              {checklistItems(reviewApp).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                    Requirement checklist
                  </p>
                  <ul className="space-y-1.5">
                    {checklistItems(reviewApp).map((item, idx) => (
                      <li
                        key={`${item.key}-${idx}`}
                        className="flex items-center gap-2 text-sm bg-gray-50 rounded-xl px-3 py-2"
                      >
                        {item.met ? (
                          <CheckCircle2 className="w-4 h-4 text-skutopia-600 shrink-0" />
                        ) : (
                          <Flag className="w-4 h-4 text-amber-600 shrink-0" />
                        )}
                        <span>
                          {item.label ||
                            requirementLabel({
                              key: String(item.key || "other"),
                            })}
                          {!item.met && (
                            <span className="text-amber-700"> — missing</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(() => {
                const profile = admissionsProfile(reviewApp);
                if (!profile && !dossierLoading) {
                  return (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                        Admissions profile
                      </p>
                      <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3">
                        No Admissions Profile entries found for this student.
                      </p>
                    </div>
                  );
                }
                if (!profile) return null;
                const exams = asList(profile.academic_exams);
                const tests = asList(profile.standardized_tests);
                const activities = asList(profile.activities);
                const impact = asList(profile.community_impact);
                const narratives = asList(profile.narratives);
                const hasAny =
                  exams.length +
                    tests.length +
                    activities.length +
                    impact.length +
                    narratives.length >
                  0;
                if (!hasAny) {
                  return (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                        Admissions profile
                      </p>
                      <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3">
                        No Admissions Profile entries found for this student.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-4">
                    <p className="text-xs uppercase tracking-wide text-gray-400">
                      Admissions profile
                    </p>
                    {exams.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1.5">
                          Exam results
                        </p>
                        <div className="space-y-2">
                          {exams.map((e, i) => (
                            <div
                              key={String(e.id || i)}
                              className="text-sm bg-gray-50 rounded-xl p-3 space-y-0.5"
                            >
                              <p className="font-medium">
                                {[e.exam_board, e.exam_level, e.exam_name]
                                  .filter(Boolean)
                                  .map(String)
                                  .join(" · ") || "Exam"}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {[
                                  e.exam_year ? `Year ${e.exam_year}` : null,
                                  e.aggregate_or_gpa
                                    ? `Aggregate/GPA: ${e.aggregate_or_gpa}`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              {e.subjects_summary ? (
                                <p className="text-gray-600 text-xs whitespace-pre-wrap">
                                  {String(e.subjects_summary)}
                                </p>
                              ) : null}
                              <DocLinks
                                docs={asDocs(e.documents)}
                                openingDoc={openingDoc}
                                onOpen={openAttachedDocument}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {tests.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1.5">
                          Standardized tests
                        </p>
                        <div className="space-y-2">
                          {tests.map((t, i) => (
                            <div
                              key={String(t.id || i)}
                              className="text-sm bg-gray-50 rounded-xl p-3"
                            >
                              <p className="font-medium">
                                {String(t.test_type || "Test")}
                                {t.overall_score != null
                                  ? ` — ${t.overall_score}`
                                  : ""}
                              </p>
                              {t.test_date ? (
                                <p className="text-xs text-gray-500">
                                  {String(t.test_date)}
                                </p>
                              ) : null}
                              <DocLinks
                                docs={asDocs(t.documents)}
                                openingDoc={openingDoc}
                                onOpen={openAttachedDocument}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {activities.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1.5">
                          Activities / experience
                        </p>
                        <div className="space-y-2">
                          {activities.map((a, i) => (
                            <div
                              key={String(a.id || i)}
                              className="text-sm bg-gray-50 rounded-xl p-3"
                            >
                              <p className="font-medium">
                                {String(a.title || "Activity")}
                              </p>
                              <p className="text-xs text-gray-500">
                                {[a.category, a.organization, a.role]
                                  .filter(Boolean)
                                  .map(String)
                                  .join(" · ")}
                              </p>
                              {a.description ? (
                                <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                                  {String(a.description)}
                                </p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {impact.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1.5">
                          Community impact
                        </p>
                        <div className="space-y-2">
                          {impact.map((c, i) => (
                            <div
                              key={String(c.id || i)}
                              className="text-sm bg-gray-50 rounded-xl p-3"
                            >
                              <p className="font-medium">
                                {String(c.organization || "Organization")}
                              </p>
                              <p className="text-xs text-gray-500">
                                {[c.role, c.hours != null ? `${c.hours} hrs` : null]
                                  .filter(Boolean)
                                  .map(String)
                                  .join(" · ")}
                              </p>
                              {(c.impact_summary || c.description) && (
                                <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                                  {String(c.impact_summary || c.description)}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {narratives.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1.5">
                          Essays & recommendations
                        </p>
                        <div className="space-y-2">
                          {narratives.map((n, i) => (
                            <div
                              key={String(n.id || i)}
                              className="text-sm bg-gray-50 rounded-xl p-3 space-y-1"
                            >
                              <p className="font-medium">
                                {String(n.type || n.title || "Narrative")}
                              </p>
                              {n.title ? (
                                <p className="text-xs text-gray-500">
                                  {String(n.title)}
                                </p>
                              ) : null}
                              {n.recommender_name ? (
                                <p className="text-xs text-gray-500">
                                  Recommender: {String(n.recommender_name)}
                                  {n.recommender_relation
                                    ? ` (${n.recommender_relation})`
                                    : ""}
                                  {n.recommender_email
                                    ? ` · ${String(n.recommender_email)}`
                                    : ""}
                                </p>
                              ) : null}
                              {n.body ? (
                                <p className="text-xs text-gray-700 whitespace-pre-wrap">
                                  {String(n.body)}
                                </p>
                              ) : null}
                              <DocLinks
                                docs={asDocs(n.documents)}
                                openingDoc={openingDoc}
                                onOpen={openAttachedDocument}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {reviewApp.cover_letter && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Cover letter</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-3">
                    {reviewApp.cover_letter}
                  </p>
                </div>
              )}

              {legacyResponseItems(reviewApp).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Application responses</p>
                  <div className="space-y-2">
                    {legacyResponseItems(reviewApp).map((item, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 rounded-xl p-3 whitespace-pre-wrap">
                        {item.text || "—"}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reviewDocuments(reviewApp).length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                    Documents
                  </p>
                  <DocLinks
                    docs={reviewDocuments(reviewApp)}
                    openingDoc={openingDoc}
                    onOpen={openAttachedDocument}
                  />
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Message student</p>
                <textarea
                  value={messageDraft}
                  onChange={(e) => setMessageDraft(e.target.value)}
                  rows={3}
                  placeholder="Ask a follow-up question or share next steps…"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    className="rounded-xl bg-skutopia-600 hover:bg-skutopia-700 text-white"
                    disabled={messageSending || !messageDraft.trim()}
                    onClick={sendMessage}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {messageSending ? "Sending…" : "Send message"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── ANALYTICS ─── */
export const PartnerAnalyticsView = () => {
  const { analytics, applications, platformLearnerCount, refresh, followers } = usePartnerData();
  const [reach, setReach] = useState<{
    platform_learners: number;
    total_views: number;
    total_applications: number;
    total_saves: number;
    total_apply_clicks: number;
    geographic: Array<{ region: string; value: number }>;
    geographic_source?: string;
  } | null>(null);
  const [reachLoading, setReachLoading] = useState(true);
  const [reachError, setReachError] = useState<string | null>(null);

  const loadReach = useCallback(async () => {
    setReachLoading(true);
    setReachError(null);
    try {
      const { data, error } = await supabase.rpc("get_partner_reach_summary");
      if (error) throw error;
      const geo = Array.isArray(data?.geographic)
        ? data.geographic
            .map((row: any) => ({
              region: String(row.region || "").trim(),
              value: Number(row.value) || 0,
            }))
            .filter((row: { region: string; value: number }) => row.region && row.value > 0)
        : [];
      setReach({
        platform_learners: Number(data?.platform_learners) || 0,
        total_views: Number(data?.total_views) || 0,
        total_applications: Number(data?.total_applications) || 0,
        total_saves: Number(data?.total_saves) || 0,
        total_apply_clicks: Number(data?.total_apply_clicks) || 0,
        geographic: geo,
        geographic_source: String(data?.geographic_source || ""),
      });
    } catch (e: unknown) {
      console.error("Reach summary failed:", e);
      setReachError("Could not load reach stats.");
      setReach(null);
    } finally {
      setReachLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReach();
  }, [loadReach, applications.length, analytics.length]);

  const fallbackGeo = useMemo(() => {
    const countryCounts: Record<string, number> = {};
    applications.forEach((app) => {
      const country = (app.country || (app as any).student_country || "").trim();
      if (!country) return;
      countryCounts[country] = (countryCounts[country] || 0) + 1;
    });
    return Object.entries(countryCounts)
      .map(([region, value]) => ({ region, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [applications]);

  const totalViews =
    reach?.total_views ??
    analytics.reduce((sum, a) => sum + (a.total_views || 0), 0);
  const totalSaves =
    reach?.total_saves ??
    analytics.reduce((sum, a) => sum + (a.total_bookmarks || 0), 0);
  const totalApplications = reach?.total_applications ?? applications.length;
  const totalApplyClicks =
    reach?.total_apply_clicks ??
    analytics.reduce((sum, a) => sum + ((a as any).total_apply_clicks || 0), 0);
  const learners = reach?.platform_learners ?? platformLearnerCount;
  const realGeographicData =
    reach?.geographic && reach.geographic.length > 0
      ? reach.geographic
      : fallbackGeo;
  const mapHint =
    reach?.geographic_source === "apply_clicks"
      ? "Where students tapped Apply on your listings"
      : "Where completed applications come from";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Live engagement across your Discover listings.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={() => {
            void refresh();
            void loadReach();
          }}
        >
          Refresh
        </Button>
      </div>

      {reachError && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
          {reachError} Showing cached portal data where available.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          {
            label: "Platform learners",
            value: reachLoading && !reach ? "…" : learners.toLocaleString(),
            hint: "Students on Skutopia",
          },
          {
            label: "Student followers",
            value: followers.length.toString(),
            hint: "Following your institution",
          },
          {
            label: "Listing views",
            value: reachLoading && !reach ? "…" : totalViews.toLocaleString(),
            hint: "Unique student views / day",
          },
          {
            label: "Apply clicks",
            value: reachLoading && !reach ? "…" : totalApplyClicks.toLocaleString(),
            hint: "Students who tapped Apply",
          },
          {
            label: "Applications",
            value: reachLoading && !reach ? "…" : totalApplications.toString(),
            hint: "Submitted to your listings",
          },
          {
            label: "Saved by students",
            value: reachLoading && !reach ? "…" : totalSaves.toLocaleString(),
            hint: "Bookmarks on your listings",
          },
        ].map((stat) => (
        <PortalCard key={stat.label} className="p-5">
          <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
          <p className="text-2xl font-semibold mb-1">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.hint}</p>
        </PortalCard>
      ))}
    </div>

      <PortalCard className="p-6 overflow-hidden">
        <h3 className="font-semibold mb-1">Geographic Distribution</h3>
        <p className="text-sm text-gray-500 mb-4">{mapHint}</p>
        <ReachWorldMap
          data={realGeographicData}
          learners={learners}
          loading={reachLoading}
          onRefresh={() => {
            void refresh();
            void loadReach();
          }}
        />
      </PortalCard>

      <PartnerFollowersPanel followers={followers} />
          </div>
  );
};

/* ─── MESSAGES ─── */
const PARTNERSHIP_CHAT_BUCKET = "partnership-chat";
const MAX_CHAT_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const isSameLocalDay = (a: string, b: string) => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const formatPartnershipDayHeader = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (startOfToday.getTime() - startOfDay.getTime()) / (24 * 60 * 60 * 1000)
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const PartnershipDayDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>
);

const formatPartnershipInboxTimestamp = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isToday) return time;
  return `${d.toLocaleDateString()} · ${time}`;
};

type PartnershipDeliveryStatus = "sent" | "delivered" | "read";

type PartnershipMessageReaction = {
  id: string;
  partnership_message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
};

const PARTNERSHIP_REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥", "👏", "🎉"];

const resolvePartnershipDelivery = (
  msg: PartnershipMessage,
  allMessages: PartnershipMessage[]
): PartnershipDeliveryStatus => {
  if (msg.is_read) return "read";
  const peerActiveAfter = allMessages.some(
    (m) =>
      m.sender_id !== msg.sender_id &&
      new Date(m.sent_at).getTime() > new Date(msg.sent_at).getTime()
  );
  if (peerActiveAfter) return "delivered";
  return "sent";
};

const PartnershipDeliveryTicks = ({
  status,
  mine,
}: {
  status: PartnershipDeliveryStatus;
  mine: boolean;
}) => {
  const muted = mine ? "text-white/70" : "text-gray-400";
  if (status === "sent") {
    return <Check className={cn("w-3 h-3", muted)} aria-hidden />;
  }
  if (status === "delivered") {
    return <CheckCheck className={cn("w-3 h-3", muted)} aria-hidden />;
  }
  return <CheckCheck className="w-3 h-3 text-sky-400" aria-hidden />;
};

const ChatAvatar = ({
  name,
  src,
  className,
}: {
  name?: string;
  src?: string | null;
  className?: string;
}) => {
  const [failed, setFailed] = useState(false);
  const initial = (name?.trim().charAt(0) || "?").toUpperCase();
  const showImage = Boolean(src) && !failed;

  return (
    <div
      className={cn(
        "rounded-full bg-gradient-to-br from-skutopia-500 to-skutopia-700 flex items-center justify-center text-white font-semibold overflow-hidden shrink-0",
        className
      )}
    >
      {showImage ? (
        <img
          src={src!}
          alt={name || "Avatar"}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initial
      )}
  </div>
  );
};

export const PartnerMessagesView = () => {
  const {
    messages,
    refresh,
    applications,
    chatPeers,
    followers,
    profile,
    markPartnershipThreadReadLocal,
    deletePartnershipMessage,
    deletePartnershipThread,
    sendFollowerMessage,
    isProPlus,
  } = usePartnerData();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRootId, setSelectedRootId] = useState<string | null>(null);
  const [composeTarget, setComposeTarget] = useState<{
    studentId: string;
    name: string;
    avatar: string | null;
  } | null>(null);
  const [replyText, setReplyText] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [reactions, setReactions] = useState<PartnershipMessageReaction[]>([]);
  const [reactionMenuId, setReactionMenuId] = useState<string | null>(null);
  const [peerTyping, setPeerTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingSessionRef = useRef<PartnershipTypingSession | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const partnerName = profile?.institution_name || "Your organization";
  const partnerAvatar = profile?.avatar_url || profile?.logo_url || null;

  const studentMeta = useCallback(
    (id?: string | null) => {
      if (!id) return { name: "Student", avatar: null as string | null };
      const app = applications.find((a) => a.student_id === id);
      const peer = chatPeers[id];
      const follower = followers.find((f) => f.student_id === id);
      return {
        name:
          app?.student_name ||
          peer?.student_name ||
          follower?.display_name ||
          "Student",
        avatar:
          app?.student_avatar_url ||
          peer?.avatar_url ||
          follower?.profile_image_url ||
          null,
      };
    },
    [applications, chatPeers, followers]
  );

  const normalizeSubject = (subject: string) =>
    subject.replace(/^Re:\s*/gi, "").trim();

  const conversations = useMemo(() => {
    const conversationMap = new Map<string, PartnershipMessage[]>();

    messages.forEach((msg) => {
      const normalizedSubject = normalizeSubject(msg.subject || "No Subject");
      const studentId =
        msg.sender_id === user?.id ? msg.receiver_id : msg.sender_id;
      const conversationKey = `${msg.partner_id || user?.id}-${studentId || "unknown"}-${normalizedSubject}`;
      
      if (!conversationMap.has(conversationKey)) {
        conversationMap.set(conversationKey, []);
      }
      conversationMap.get(conversationKey)!.push(msg);
    });
    
    return Array.from(conversationMap.values())
      .map((conv) => {
        const sorted = [...conv].sort(
          (a, b) =>
        new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
      );
      const root = sorted[0];
        const latest = sorted[sorted.length - 1];
        const studentId =
          root.sender_id === user?.id ? root.receiver_id : root.sender_id;
        const student = studentMeta(studentId);
          const unreadCount = sorted.filter(
            (m) =>
              !m.deleted_at &&
              !m.is_read &&
              m.sender_id !== user?.id &&
              m.receiver_id === user?.id
          ).length;
      
      return {
          root,
          latest,
        allMessages: sorted,
          studentId,
          studentName: student.name,
          studentAvatar: student.avatar,
          unreadCount,
          preview:
            latest.deleted_at
              ? "This message was deleted"
              : latest.content?.trim() ||
                (latest.attachment_name
                  ? `📎 ${latest.attachment_name}`
                  : "Attachment"),
        };
      })
      .sort(
        (a, b) =>
          new Date(b.latest.sent_at).getTime() -
          new Date(a.latest.sent_at).getTime()
      );
  }, [messages, studentMeta, user?.id]);

  // Deep-link from Student followers → Contact
  useEffect(() => {
    const withId = searchParams.get("with");
    if (!withId) return;

    const match = conversations.find((c) => c.studentId === withId);
    if (match) {
      setSelectedRootId(match.root.id);
      setComposeTarget(null);
    } else if (isProPlus) {
      const meta = studentMeta(withId);
      setSelectedRootId(null);
      setComposeTarget({
        studentId: withId,
        name: searchParams.get("name") || meta.name,
        avatar: meta.avatar,
      });
    } else {
      toast.message("Pro+ feature", {
        description: "Upgrade to Pro+ to message student followers.",
      });
    }

    setSearchParams({}, { replace: true });
  }, [searchParams, conversations, isProPlus, studentMeta, setSearchParams]);

  // If a follower compose target later matches a loaded thread, open that thread.
  useEffect(() => {
    if (!composeTarget) return;
    const match = conversations.find((c) => c.studentId === composeTarget.studentId);
    if (match) {
      setSelectedRootId(match.root.id);
      setComposeTarget(null);
    }
  }, [composeTarget, conversations]);

  const selectedConversation =
    conversations.find((c) => c.root.id === selectedRootId) || null;

  useEffect(() => {
    if (selectedRootId && !selectedConversation && conversations.length > 0) {
      const stillThere = conversations.some((c) => c.root.id === selectedRootId);
      if (!stillThere) setSelectedRootId(conversations[0].root.id);
    }
  }, [conversations, selectedConversation, selectedRootId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedConversation?.allMessages.length, selectedRootId]);

  const loadReactions = useCallback(async (messageIds: string[]) => {
    if (!messageIds.length) {
      setReactions([]);
      return;
    }
    const { data, error } = await supabase
      .from("partnership_message_reactions")
      .select("*")
      .in("partnership_message_id", messageIds);
    if (!error) {
      setReactions((data as PartnershipMessageReaction[]) ?? []);
    }
  }, []);

  useEffect(() => {
    const ids = selectedConversation?.allMessages.map((m) => m.id) ?? [];
    void loadReactions(ids);
  }, [selectedConversation?.allMessages, loadReactions]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`partnership_message_reactions_partner_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "partnership_message_reactions",
        },
        () => {
          const ids = selectedConversation?.allMessages.map((m) => m.id) ?? [];
          void loadReactions(ids);
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, selectedConversation?.allMessages, loadReactions]);

  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user?.id) return;
      try {
        const { data: existing } = await supabase
          .from("partnership_message_reactions")
          .select("id, emoji")
          .eq("partnership_message_id", messageId)
          .eq("user_id", user.id)
          .maybeSingle();

        if (existing) {
          if (existing.emoji === emoji) {
            await supabase
              .from("partnership_message_reactions")
              .delete()
              .eq("id", existing.id);
          } else {
            await supabase
              .from("partnership_message_reactions")
              .update({ emoji })
              .eq("id", existing.id);
          }
        } else {
          await supabase.from("partnership_message_reactions").insert({
            partnership_message_id: messageId,
            user_id: user.id,
            emoji,
          });
        }
        const ids = selectedConversation?.allMessages.map((m) => m.id) ?? [];
        await loadReactions(ids);
        setReactionMenuId(null);
      } catch {
        toast.error("Could not update reaction");
      }
    },
    [user?.id, selectedConversation?.allMessages, loadReactions]
  );

  useEffect(() => {
    let active = true;
    const conv = selectedConversation;
    if (!conv?.studentId || !user?.id) {
      setPeerTyping(false);
      void typingSessionRef.current?.unsubscribe();
      typingSessionRef.current = null;
      return;
    }

    const partnerId = conv.root.partner_id || user.id;
    const subject = normalizeSubject(conv.root.subject || "Conversation");
    void typingSessionRef.current?.unsubscribe();
    typingSessionRef.current = joinPartnershipTyping(
      partnerId,
      conv.studentId,
      subject,
      user.id,
      (typing) => {
        if (active) setPeerTyping(typing);
      }
    );

    return () => {
      active = false;
      void typingSessionRef.current?.unsubscribe();
      typingSessionRef.current = null;
      setPeerTyping(false);
    };
  }, [selectedConversation?.root.id, selectedConversation?.studentId, user?.id]);

  const handleReplyTextChange = (value: string) => {
    setReplyText(value);
    const session = typingSessionRef.current;
    if (!session) return;
    void session.sendTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      void session.sendTyping(false);
    }, 2000);
  };

  const markSelectedThreadRead = useCallback(
    (conv: (typeof conversations)[number]) => {
      if (!conv?.studentId || !user?.id) return;
      const partnerId = conv.root.partner_id || user.id;
      markPartnershipThreadReadLocal(
        partnerId,
        conv.studentId,
        conv.root.subject
      );
      void supabase.rpc("mark_partnership_thread_read", {
        p_partner_id: partnerId,
        p_other_user_id: conv.studentId,
        p_subject: conv.root.subject || null,
      });
    },
    [markPartnershipThreadReadLocal, user?.id]
  );

  useEffect(() => {
    if (!selectedConversation) return;
    markSelectedThreadRead(selectedConversation);
  }, [
    selectedRootId,
    selectedConversation?.root.id,
    selectedConversation?.unreadCount,
    selectedConversation?.latest?.sent_at,
    markSelectedThreadRead,
  ]);

  const uploadChatAttachment = async (file: File, uploaderId: string) => {
    const safeName = file.name
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 80);
    const path = `${uploaderId}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage
      .from(PARTNERSHIP_CHAT_BUCKET)
      .upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      });
    if (error) throw error;
    const {
      data: { publicUrl },
    } = supabase.storage.from(PARTNERSHIP_CHAT_BUCKET).getPublicUrl(path);
    return {
      url: publicUrl,
      name: file.name,
      mime: file.type || null,
      size: file.size,
    };
  };

  const handleAttachmentPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
      toast.error("File must be 15MB or smaller.");
      return;
    }
    setAttachment(file);
  };

  const handleSendMessage = async () => {
    if ((!replyText.trim() && !attachment) || !user?.id) return;

    // New follower outreach (no existing thread yet)
    if (composeTarget && !selectedConversation) {
      if (!isProPlus) {
        toast.error("Direct follower contact requires Pro+.");
        return;
      }
      if (attachment) {
        toast.error("Send a text message first, then attach files in the thread.");
        return;
      }
      setSending(true);
      try {
        const { error, messageId } = await sendFollowerMessage({
          studentId: composeTarget.studentId,
          content: replyText.trim(),
          subject: `Message from ${partnerName}`,
        });
        if (error) throw error;
        setReplyText("");
        setComposeTarget(null);
        if (messageId) setSelectedRootId(messageId);
        if (refresh) void refresh();
        toast.success("Message sent");
      } catch (error) {
        console.error("Error starting follower chat:", error);
        toast.error(
          error instanceof Error ? error.message : "Failed to send message."
        );
      } finally {
        setSending(false);
      }
      return;
    }

    if (!selectedConversation) return;

    const otherId = selectedConversation.studentId;
    if (!otherId) {
      toast.error("Could not find the student in this thread.");
        return;
      }

    setSending(true);
    try {
      let attachmentMeta: {
        url: string;
        name: string;
        mime: string | null;
        size: number;
      } | null = null;
      if (attachment) {
        attachmentMeta = await uploadChatAttachment(attachment, user.id);
      }

      const { error } = await supabase.from("partnership_messages").insert({
          sender_id: user.id,
        receiver_id: otherId,
        partner_id: selectedConversation.root.partner_id || user.id,
        application_id: selectedConversation.root.application_id,
        subject: selectedConversation.root.subject,
        content: replyText.trim(),
        message_type: "direct",
          is_from_system: false,
          is_read: false,
        parent_message_id: selectedConversation.root.id,
        attachment_url: attachmentMeta?.url ?? null,
        attachment_name: attachmentMeta?.name ?? null,
        attachment_mime_type: attachmentMeta?.mime ?? null,
        attachment_size: attachmentMeta?.size ?? null,
      });

      if (error) throw error;

      setReplyText("");
      setAttachment(null);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      void typingSessionRef.current?.sendTyping(false);
      // Realtime will refresh; keep a soft refresh as fallback.
      if (refresh) void refresh();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const isImageAttachment = (mime?: string | null, name?: string | null) => {
    if (mime?.startsWith("image/")) return true;
    return Boolean(name && /\.(jpg|jpeg|png|gif|webp)$/i.test(name));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-12rem)] min-h-[520px]">
      <PortalCard className="lg:col-span-1 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
          <h3 className="font-semibold">Inbox</h3>
            <p className="text-[11px] text-emerald-600 mt-0.5">Live updates on</p>
        </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.length > 0 ? (
            conversations.map((conv) => {
              const active = selectedRootId === conv.root.id;
              return (
              <button
                key={conv.root.id}
                type="button"
                  onClick={() => {
                    setSelectedRootId(conv.root.id);
                    setComposeTarget(null);
                    markSelectedThreadRead(conv);
                  }}
                className={cn(
                    "w-full text-left px-3 py-3 flex gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-50 dark:border-gray-800/60",
                    active && "bg-skutopia-50/70 dark:bg-skutopia-950/30"
                  )}
                >
                  <ChatAvatar
                    name={conv.studentName}
                    src={conv.studentAvatar}
                    className="h-11 w-11 text-sm"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold truncate">
                        {conv.studentName}
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {conv.unreadCount > 0 && (
                          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-skutopia-600 text-white text-[10px] font-bold flex items-center justify-center">
                            {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
                  </span>
                        )}
                  <span className="text-[10px] text-gray-400 text-right leading-tight whitespace-nowrap">
                    {formatPartnershipInboxTimestamp(conv.latest.sent_at)}
                  </span>
                </div>
                  </div>
                    <p className="text-xs text-gray-500 truncate">
                      {normalizeSubject(conv.root.subject || "Conversation")}
                    </p>
                    <p
                      className={cn(
                        "text-xs truncate mt-0.5",
                        conv.unreadCount > 0
                          ? "text-gray-800 dark:text-gray-200 font-medium"
                          : "text-gray-400"
                      )}
                    >
                      {conv.preview}
                    </p>
                  </div>
              </button>
              );
            })
          ) : (
            <div className="p-6 text-center text-sm text-gray-500">
              No conversations yet. Students can message you from Discover.
            </div>
          )}
        </div>
      </PortalCard>

      <PortalCard className="lg:col-span-2 flex flex-col overflow-hidden">
        {selectedConversation ? (
          <>
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
              <ChatAvatar
                name={selectedConversation.studentName}
                src={selectedConversation.studentAvatar}
                className="h-10 w-10 text-sm"
              />
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold truncate">
                  {selectedConversation.studentName}
                </h3>
                <p className="text-xs text-gray-500 truncate">
                  {normalizeSubject(
                    selectedConversation.root.subject || "Conversation"
                  )}
            </p>
          </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 shrink-0"
                title="Delete conversation"
                onClick={async () => {
                  if (
                    !selectedConversation.studentId ||
                    !window.confirm(
                      `Delete this conversation with ${selectedConversation.studentName}?`
                    )
                  ) {
                    return;
                  }
                  const { error } = await deletePartnershipThread(
                    selectedConversation.studentId,
                    selectedConversation.root.subject
                  );
                  if (error) toast.error("Could not delete conversation.");
                  else {
                    toast.success("Conversation deleted");
                    setSelectedRootId(null);
                    await refresh();
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
          </Button>
              </div>
              
            <div className="flex-1 overflow-y-auto px-4 py-5 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-950/40 dark:to-gray-900">
                {selectedConversation.allMessages.map((msg, index) => {
                const prev = selectedConversation.allMessages[index - 1];
                const showDay =
                  index === 0 || !isSameLocalDay(prev.sent_at, msg.sent_at);
                const mine = msg.sender_id === user?.id;
                const isDeleted = !!msg.deleted_at;
                const meta = mine
                  ? { name: partnerName, avatar: partnerAvatar }
                  : studentMeta(msg.sender_id);
                const hasImage =
                  !isDeleted &&
                  isImageAttachment(
                    msg.attachment_mime_type,
                    msg.attachment_name
                  );
                const delivery =
                  mine && !isDeleted
                    ? resolvePartnershipDelivery(
                        msg,
                        selectedConversation.allMessages
                      )
                    : null;
                const msgReactions = isDeleted
                  ? []
                  : reactions.filter(
                      (r) => r.partnership_message_id === msg.id
                    );
                const groupedReactions = msgReactions.reduce<
                  Record<string, PartnershipMessageReaction[]>
                >((acc, reaction) => {
                  acc[reaction.emoji] = acc[reaction.emoji] || [];
                  acc[reaction.emoji].push(reaction);
                  return acc;
                }, {});
                  
                  return (
                  <div
                      key={msg.id}
                      className={cn(
                      "mb-3",
                      mine ? "flex flex-col items-end" : "flex flex-col items-start"
                    )}
                  >
                    {showDay && (
                      <PartnershipDayDivider
                        label={formatPartnershipDayHeader(msg.sent_at)}
                      />
                    )}
                    <div className="relative max-w-[85%]">
                      <div
                        className={cn(
                          "flex gap-2",
                          mine ? "ml-auto flex-row-reverse" : "mr-auto"
                        )}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (isDeleted) return;
                          setReactionMenuId(
                            reactionMenuId === msg.id ? null : msg.id
                          );
                        }}
                      >
                    <ChatAvatar
                      name={meta.name}
                      src={meta.avatar}
                      className="h-8 w-8 text-xs mt-1"
                    />
                    <div
                      className={cn(
                        "rounded-2xl px-3.5 py-2.5 shadow-sm",
                        isDeleted
                          ? "bg-gray-100 dark:bg-gray-800/80 border border-dashed border-gray-300 dark:border-gray-600 rounded-2xl"
                          : mine
                            ? "bg-skutopia-600 text-white rounded-br-md"
                            : "bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-bl-md"
                      )}
                    >
                      {!isDeleted && (
                      <div
                        className={cn(
                          "flex items-center gap-2 mb-1",
                          mine ? "justify-end" : "justify-start"
                        )}
                      >
                        <span
                          className={cn(
                            "text-[11px] font-semibold",
                            mine ? "text-white/90" : "text-gray-700 dark:text-gray-200"
                          )}
                        >
                          {meta.name}
                          </span>
                        <span
                          className={cn(
                            "text-[10px] inline-flex items-center gap-1",
                            mine ? "text-white/70" : "text-gray-400"
                          )}
                        >
                          {new Date(msg.sent_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {delivery ? (
                            <PartnershipDeliveryTicks
                              status={delivery}
                              mine={mine}
                            />
                          ) : null}
                          </span>
                      </div>
                      )}
                      {isDeleted ? (
                        <p className="text-sm italic text-gray-500 dark:text-gray-400">
                          This message was deleted
                        </p>
                      ) : (
                        <>
                      {msg.attachment_url && (
                        <div className="mb-2">
                          {hasImage ? (
                            <a
                              href={msg.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={msg.attachment_url}
                                alt={msg.attachment_name || "Image"}
                                className="max-h-56 max-w-full rounded-xl object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={msg.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                "inline-flex items-center gap-2 text-xs font-medium underline",
                                mine ? "text-white" : "text-skutopia-700"
                              )}
                            >
                              <Paperclip className="w-3.5 h-3.5" />
                              {msg.attachment_name || "Document"}
                            </a>
                          )}
                        </div>
                      )}
                      {msg.content?.trim() ? (
                        <p
                          className={cn(
                            "text-sm whitespace-pre-wrap leading-relaxed",
                            mine ? "text-white" : "text-gray-800 dark:text-gray-100"
                          )}
                        >
                          {msg.content}
                        </p>
                      ) : null}
                        </>
                      )}
                      </div>
                      </div>
                      {reactionMenuId === msg.id && !isDeleted && (
                        <div
                          className={cn(
                            "absolute z-20 mt-1 flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 shadow-lg",
                            mine ? "right-10" : "left-10"
                          )}
                        >
                          {PARTNERSHIP_REACTION_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="text-lg leading-none p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                              onClick={() => void toggleReaction(msg.id, emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="ml-1 p-1.5 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                            title="Delete message"
                            onClick={async () => {
                              if (!window.confirm("Delete this message?")) return;
                              const { error } = await deletePartnershipMessage(msg.id);
                              if (error) toast.error("Could not delete message.");
                              else {
                                toast.success("Message deleted");
                                setReactionMenuId(null);
                              }
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {Object.keys(groupedReactions).length > 0 && (
                        <div
                          className={cn(
                            "flex flex-wrap gap-1.5 mt-1",
                            mine ? "justify-end pr-10" : "justify-start pl-10"
                          )}
                        >
                          {Object.entries(groupedReactions).map(
                            ([emoji, items]) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => void toggleReaction(msg.id, emoji)}
                                className={cn(
                                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold",
                                  items.some((r) => r.user_id === user?.id)
                                    ? "border-skutopia-300 bg-skutopia-50 text-skutopia-800 dark:bg-skutopia-950/40 dark:text-skutopia-200"
                                    : "border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                )}
                              >
                                <span>{emoji}</span>
                                <span>{items.length}</span>
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              {peerTyping && selectedConversation && (
                <div className="px-1 pb-2">
                  <div className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-xs italic text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                    <span>{selectedConversation.studentName} is typing</span>
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
                    </span>
                  </div>
                </div>
              )}
                <div ref={messagesEndRef} />
              </div>
              
            <div className="p-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              {attachment && (
                <div className="flex items-center gap-2 text-xs bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2">
                  {attachment.type.startsWith("image/") ? (
                    <ImageIcon className="w-4 h-4 text-skutopia-600" />
                  ) : (
                    <Paperclip className="w-4 h-4 text-skutopia-600" />
                  )}
                  <span className="truncate flex-1">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    className="p-1 rounded hover:bg-white dark:hover:bg-gray-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                    </div>
              )}
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,application/pdf"
                  onChange={handleAttachmentPick}
                />
                    <Button 
                  type="button"
                      variant="outline"
                  size="icon"
                  className="rounded-xl shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach image or document"
                >
                  <Paperclip className="w-4 h-4" />
                    </Button>
                    <textarea
                      value={replyText}
                  onChange={(e) => handleReplyTextChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                  placeholder="Write a message…"
                  rows={1}
                  className="flex-1 min-h-[44px] max-h-32 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-skutopia-500 dark:bg-gray-800"
                />
                    <Button 
                  onClick={() => void handleSendMessage()}
                  disabled={sending || (!replyText.trim() && !attachment)}
                  className="rounded-xl bg-skutopia-600 hover:bg-skutopia-700 text-white shrink-0"
                >
                  <Send className="w-4 h-4" />
                    </Button>
                    </div>
                  </div>
          </>
          ) : composeTarget ? (
          <>
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <ChatAvatar
                  name={composeTarget.name}
                  src={composeTarget.avatar}
                  className="h-10 w-10 text-sm"
                />
                <div className="min-w-0">
                  <p className="font-semibold truncate">{composeTarget.name}</p>
                  <p className="text-xs text-gray-500">New follower message</p>
                </div>
              </div>
                    <Button 
                type="button"
                variant="ghost"
                size="sm"
                      className="rounded-xl"
                onClick={() => setComposeTarget(null)}
                    >
                <X className="w-4 h-4" />
                    </Button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center text-sm text-gray-500 gap-2 p-8">
              <MessageSquare className="w-8 h-8 text-gray-300" />
              <p className="text-center max-w-sm">
                Write a message to start a conversation with this follower.
              </p>
            </div>
            <div className="p-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-end gap-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                  placeholder={`Message ${composeTarget.name}…`}
                  rows={1}
                  className="flex-1 min-h-[44px] max-h-32 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-skutopia-500 dark:bg-gray-800"
                />
                    <Button 
                  onClick={() => void handleSendMessage()}
                  disabled={sending || !replyText.trim()}
                  className="rounded-xl bg-skutopia-600 hover:bg-skutopia-700 text-white shrink-0"
                >
                  <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
          </>
          ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-sm text-gray-500 gap-2 p-8">
            <MessageSquare className="w-8 h-8 text-gray-300" />
            <p>Select a conversation to start chatting</p>
            <p className="text-xs text-gray-400 text-center max-w-xs">
              Or open Reach / Organization and contact a student follower (Pro+).
            </p>
            </div>
          )}
      </PortalCard>
    </div>
  );
};

/* ─── ORGANIZATION ─── */
export const PartnerOrganizationView = () => {
  const { profile, programs, applications, followers, user, updateProfile, saveInstitutionName } = usePartnerData();
  const { refreshUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    institution_name: '',
    institution_type: 'University',
    mission: '',
    history: '',
    website: '',
    email: '',
    phone: '',
    country: '',
    locations: '',
  });
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [detectingCountry, setDetectingCountry] = useState(false);
  const [countrySource, setCountrySource] = useState<"profile" | "live" | null>(null);
  const liveCountryRef = useRef<string | null>(null);

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>("");
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [bannerPicture, setBannerPicture] = useState<File | null>(null);
  const [bannerPicturePreview, setBannerPicturePreview] = useState<string>("");
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [bannerSuccess, setBannerSuccess] = useState(false);
  const bannerFileInputRef = useRef<HTMLInputElement>(null);

  // Sync form state with profile data when it changes
  useEffect(() => {
    if (profile) {
      setFormData({
        institution_name: profile.institution_name || '',
        institution_type: (profile as Record<string, any>).institution_type || 'University',
        mission: profile.mission || '',
        history: profile.history || '',
        website: profile.website || '',
        email: profile.email || user?.email || '',
        phone: profile.phone || '',
        country: liveCountryRef.current || profile.country || '',
        locations: Array.isArray(profile.locations) 
          ? profile.locations.join(', ') 
          : (profile.locations || ''),
      });
      setCountrySource(liveCountryRef.current ? "live" : profile.country ? "profile" : null);
    }
  }, [profile, user?.email]);

  // Prefill Country from the partner's current device location
  useEffect(() => {
    let cancelled = false;
    setDetectingCountry(true);
    void (async () => {
      const loc = await detectUserLocation();
      if (cancelled || !loc?.country) {
        if (!cancelled) setDetectingCountry(false);
        return;
      }
      liveCountryRef.current = loc.country;
      setFormData((prev) => ({ ...prev, country: loc.country }));
      setCountrySource("live");
      setDetectingCountry(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyCurrentLocation = async () => {
    setDetectingCountry(true);
    const loc = await detectUserLocation();
    setDetectingCountry(false);
    if (!loc?.country) {
      toast.error("Could not detect your location. Allow location access and try again.");
      return;
    }
    liveCountryRef.current = loc.country;
    setFormData((prev) => ({ ...prev, country: loc.country }));
    setCountrySource("live");
    toast.success(`Country set to ${loc.country}`);
  };

  const handleProfilePictureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB");
      return;
    }
    setProfilePicture(file);
    setUploadError("");
    setUploadSuccess(false);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleProfilePictureUpload = async () => {
    if (!profilePicture || !user?.id) return;
    setUploadingProfile(true);
    setUploadError("");
    setUploadSuccess(false);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error("Your session expired. Please log in again, then upload the logo.");
      }
      const ownerId = session.user.id;

      const fileExt = profilePicture.name.split(".").pop()?.toLowerCase() || "jpg";
      const safeExt = ["jpg", "jpeg", "png", "webp", "gif"].includes(fileExt) ? fileExt : "jpg";
      const contentType =
        profilePicture.type && profilePicture.type.startsWith("image/")
          ? profilePicture.type === "image/jpg"
            ? "image/jpeg"
            : profilePicture.type
          : safeExt === "png"
            ? "image/png"
            : safeExt === "webp"
              ? "image/webp"
              : safeExt === "gif"
                ? "image/gif"
                : "image/jpeg";
      const fileName = `${ownerId}-${Date.now()}.${safeExt === "jpeg" ? "jpg" : safeExt}`;
      const filePath = `${ownerId}/${fileName}`;

      const { error: uploadErr } = await supabase.storage
        .from("partner-profile-pictures")
        .upload(filePath, profilePicture, {
          upsert: false,
          contentType,
          cacheControl: "3600",
        });
      if (uploadErr) throw uploadErr;

      const {
        data: { publicUrl },
      } = supabase.storage.from("partner-profile-pictures").getPublicUrl(filePath);

      const { error: updateErr } = await supabase
        .from("partners")
        .update({
          avatar_url: publicUrl,
          logo_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", ownerId);
      if (updateErr) throw updateErr;

      setUploadSuccess(true);
      setProfilePicture(null);
      setProfilePicturePreview("");
      if (updateProfile) await updateProfile();
    } catch (error: any) {
      console.error("Profile picture upload error:", error);
      setUploadError(error.message || "Failed to upload logo");
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!user?.id) return;
    try {
      // Clear both fields — Flutter falls back to logo_url when avatar_url is null.
      const { error } = await supabase
        .from('partners')
        .update({ avatar_url: null, logo_url: null })
        .eq('id', user.id);
      if (error) throw error;
      if (updateProfile) await updateProfile();
    } catch (error: any) {
      console.error('Profile picture removal error:', error);
      setUploadError(error.message || "Failed to remove logo");
    }
  };

  const handleBannerSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setBannerError("Please select an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setBannerError("File size must be less than 8MB");
      return;
    }
    setBannerPicture(file);
    setBannerError("");
    setBannerSuccess(false);
    const reader = new FileReader();
    reader.onloadend = () => setBannerPicturePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleBannerUpload = async () => {
    if (!bannerPicture || !user?.id) return;
    setUploadingBanner(true);
    setBannerError("");
    setBannerSuccess(false);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error("Your session expired. Please log in again, then upload the banner.");
      }
      const ownerId = session.user.id;

      const fileExt = bannerPicture.name.split(".").pop()?.toLowerCase() || "jpg";
      const safeExt = ["jpg", "jpeg", "png", "webp", "gif"].includes(fileExt) ? fileExt : "jpg";
      const contentType =
        bannerPicture.type && bannerPicture.type.startsWith("image/")
          ? bannerPicture.type === "image/jpg"
            ? "image/jpeg"
            : bannerPicture.type
          : safeExt === "png"
            ? "image/png"
            : safeExt === "webp"
              ? "image/webp"
              : safeExt === "gif"
                ? "image/gif"
                : "image/jpeg";
      const fileName = `${ownerId}-banner-${Date.now()}.${safeExt === "jpeg" ? "jpg" : safeExt}`;
      const filePath = `${ownerId}/${fileName}`;

      const { error: uploadErr } = await supabase.storage
        .from("partner-profile-pictures")
        .upload(filePath, bannerPicture, {
          upsert: false,
          contentType,
          cacheControl: "3600",
        });
      if (uploadErr) throw uploadErr;

      const {
        data: { publicUrl },
      } = supabase.storage.from("partner-profile-pictures").getPublicUrl(filePath);

      const { error: updateErr } = await supabase
        .from("partners")
        .update({
          banner_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", ownerId);
      if (updateErr) throw updateErr;

      setBannerSuccess(true);
      setBannerPicture(null);
      setBannerPicturePreview("");
      if (updateProfile) await updateProfile();
    } catch (error: any) {
      console.error("Banner upload error:", error);
      setBannerError(error.message || "Failed to upload background banner");
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRemoveBanner = async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('partners')
        .update({ banner_url: null })
        .eq('id', user.id);
      if (error) throw error;
      if (updateProfile) await updateProfile();
    } catch (error: any) {
      console.error('Banner removal error:', error);
      setBannerError(error.message || "Failed to remove banner");
    }
  };

  // Calculate real impact metrics from database data
  const calculateImpactMetrics = () => {
    const learnersSupported = applications.length; // Count applications as learners supported
    const opportunitiesPublished = programs.filter(p => p.is_active).length; // Count active programs
    
    // Calculate years partnering from created_at date
    let yearsPartnering = 0;
    if (profile?.created_at) {
      const createdDate = new Date(profile.created_at);
      const now = new Date();
      const years = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24 * 365));
      yearsPartnering = years > 0 ? years : 1; // Minimum 1 year if created
    }

    // Unique applicant countries (avoid counting the same country twice)
    const countriesReached = new Set(
      applications
        .map((a) => (a.country || "").trim())
        .filter((c) => c.length > 0)
    ).size;

    return {
      learnersSupported: learnersSupported > 0 ? `${learnersSupported.toLocaleString()}+` : '0',
      countriesReached: countriesReached > 0 ? countriesReached.toString() : '0',
      opportunitiesPublished: opportunitiesPublished.toString(),
      yearsPartnering: yearsPartnering.toString(),
      studentFollowers: followers.length.toString(),
    };
  };

  const impactMetrics = calculateImpactMetrics();

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user?.id) return;

    const locationsArray = formData.locations
      ? formData.locations.split(",").map((loc) => loc.trim()).filter((loc) => loc)
      : [];

    const previewForValidation = {
      institution_name: formData.institution_name,
      mission: formData.mission,
      history: formData.history,
      website: formData.website,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      locations: locationsArray,
      avatar_url: profile?.avatar_url,
      logo_url: profile?.logo_url,
    };
    const missing = getPartnerProfileMissingFields(previewForValidation);
    // Allow saving partial drafts, but require logo before marking "complete" path messaging.
    // Hard-require all public fields so admins can verify after save.
    if (missing.length > 0) {
      toast.error(`Fill all public profile fields before saving: ${missing.join(", ")}`);
      setSaveStatus("error");
      return;
    }
    
    setLoading(true);
    setSaveStatus("idle");

    try {
      const { error: institutionError } = await saveInstitutionName(formData.institution_name);
      if (institutionError) {
        throw institutionError;
      }

      const { data, error } = await supabase
        .from("partners")
        .update({ 
          institution_type: formData.institution_type || 'University',
          website: formData.website.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          mission: formData.mission.trim(),
          history: formData.history.trim(),
          country: formData.country.trim() || null,
          locations: locationsArray,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      
      setSaveStatus("success");
      toast.success(
        profile?.is_verified
          ? "Profile updated."
          : "Profile saved. An admin can now verify your account so you can publish listings.",
      );

      if (data && data.length > 0) {
        const updatedProfile = data[0];
        setFormData({
          institution_name: updatedProfile.institution_name || formData.institution_name,
          institution_type: updatedProfile.institution_type || formData.institution_type || "University",
          mission: updatedProfile.mission || "",
          history: updatedProfile.history || "",
          website: updatedProfile.website || "",
          email: updatedProfile.email || formData.email || "",
          phone: updatedProfile.phone || formData.phone || "",
          country: updatedProfile.country || "",
          locations: Array.isArray(updatedProfile.locations) 
            ? updatedProfile.locations.join(", ")
            : updatedProfile.locations || "",
        });
      }
      
      if (updateProfile) await updateProfile();
      await refreshUserProfile();

      setTimeout(() => {
        setSaveStatus("idle");
      }, 3000);
    } catch (error) {
      console.error("Error updating profile:", error);
      setSaveStatus("error");
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isVerified = Boolean(profile?.is_verified);
  const profileComplete = isPartnerProfileComplete(profile);
  const missingProfileFields = getPartnerProfileMissingFields(profile);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Fill in every field below and save. After that, Skutopia can verify your organization so
        you can publish listings.
      </p>

      <VerificationGateBanner
        isVerified={isVerified}
        profileComplete={profileComplete}
        missing={missingProfileFields}
      />
      {isVerified ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          Your organization is verified. You can publish program listings.
        </div>
      ) : null}

      <PortalCard className="p-6 sm:p-8">
        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          {/* Logo / profile picture uploader */}
          <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 block">
              Organization Logo
            </Label>
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 shrink-0">
                <div className="h-20 w-20 rounded-2xl overflow-hidden bg-gradient-to-br from-skutopia-500 to-skutopia-700 flex items-center justify-center text-white text-xl font-bold shadow-sm">
                  {profilePicturePreview ? (
                    <img src={profilePicturePreview} alt="Logo preview" className="w-full h-full object-cover" />
                  ) : profile?.avatar_url || profile?.logo_url ? (
                    <img src={profile.avatar_url || profile.logo_url} alt="Organization logo" className="w-full h-full object-cover" />
                  ) : (
                    (profile?.institution_name || "O").charAt(0)
                  )}
          </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingProfile}
                  className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-skutopia-600 hover:border-skutopia-300 transition-colors"
                  title="Change logo"
                  aria-label="Change logo"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureSelect}
                  className="hidden"
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Square image (PNG, JPG, WebP) for profile avatar and chat.
                </p>
                {profilePicture ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleProfilePictureUpload}
                      disabled={uploadingProfile}
                      className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-lg text-xs"
                    >
                      {uploadingProfile ? "Saving…" : "Save logo"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-xs"
                      onClick={() => {
                        setProfilePicture(null);
                        setProfilePicturePreview("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  (profile?.avatar_url || profile?.logo_url) && (
                    <button
                      type="button"
                      onClick={handleRemoveProfilePicture}
                      className="text-xs text-red-600 hover:underline font-medium"
                    >
                      Remove logo
                    </button>
                  )
                )}
              </div>
            </div>
            {uploadError && (
              <p className="mt-2 flex items-center gap-1.5 text-red-600 text-xs">
                <X className="w-3.5 h-3.5" /> {uploadError}
              </p>
            )}
            {uploadSuccess && (
              <p className="mt-2 flex items-center gap-1.5 text-emerald-600 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Logo updated
              </p>
            )}
          </div>

          {/* Background banner / cover image uploader */}
          <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 block">
              Background Cover Banner
            </Label>
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-32 shrink-0">
                <div className="h-20 w-32 rounded-2xl overflow-hidden bg-gradient-to-r from-emerald-800 via-teal-900 to-emerald-950 flex items-center justify-center text-white text-xs font-medium shadow-sm">
                  {bannerPicturePreview ? (
                    <img src={bannerPicturePreview} alt="Banner preview" className="w-full h-full object-cover" />
                  ) : profile?.banner_url ? (
                    <img src={profile.banner_url} alt="Banner preview" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] text-white/70 px-2 text-center">Default Green Theme</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => bannerFileInputRef.current?.click()}
                  disabled={uploadingBanner}
                  className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-skutopia-600 hover:border-skutopia-300 transition-colors"
                  title="Change banner"
                  aria-label="Change banner"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
                <input
                  ref={bannerFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerSelect}
                  className="hidden"
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Wide banner displayed at the top of your institution profile.
                </p>
                {bannerPicture ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleBannerUpload}
                      disabled={uploadingBanner}
                      className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-lg text-xs"
                    >
                      {uploadingBanner ? "Saving…" : "Save banner"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-xs"
                      onClick={() => {
                        setBannerPicture(null);
                        setBannerPicturePreview("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  profile?.banner_url && (
                    <button
                      type="button"
                      onClick={handleRemoveBanner}
                      className="text-xs text-red-600 hover:underline font-medium"
                    >
                      Remove banner
                    </button>
                  )
                )}
              </div>
            </div>
            {bannerError && (
              <p className="mt-2 flex items-center gap-1.5 text-red-600 text-xs">
                <X className="w-3.5 h-3.5" /> {bannerError}
              </p>
            )}
            {bannerSuccess && (
              <p className="mt-2 flex items-center gap-1.5 text-emerald-600 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Banner updated
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-100 dark:border-gray-800">
          <div className="min-w-0">
            <h3 className="text-xl font-semibold mb-1 truncate">{profile?.institution_name || "Your Organization"}</h3>
            <p className="text-sm text-gray-500">
              Partner since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "Not set"}
            </p>
          </div>
            <div className="flex flex-wrap gap-2">
            {isVerified ? (
              <span className="text-xs px-2.5 py-1 rounded-full bg-skutopia-50 text-skutopia-700 font-medium">
                Verified Partner
              </span>
            ) : profileComplete ? (
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 font-medium">
                Awaiting verification
              </span>
            ) : (
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                Profile incomplete
              </span>
            )}
            <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
              {(profile as Record<string, any>)?.institution_type || "University"}
              </span>
            </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-sm font-medium mb-2 block">Institution name</Label>
            <input
              type="text"
              value={formData.institution_name}
              onChange={(e) => handleChange('institution_name', e.target.value)}
              placeholder="e.g. Skutopia Academy"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Institution type</Label>
            <select
              value={formData.institution_type}
              onChange={(e) => handleChange('institution_type', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            >
              <option value="University">University</option>
              <option value="High school">High school</option>
              <option value="Bootcamp">Bootcamp</option>
              <option value="Study abroad">Study abroad</option>
              <option value="College & Vocational">College & Vocational</option>
              <option value="Academy / Institute">Academy / Institute</option>
              <option value="Organization / Foundation">Organization / Foundation</option>
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <Label className="text-sm font-medium">Country</Label>
              <button
                type="button"
                onClick={() => void applyCurrentLocation()}
                disabled={detectingCountry}
                className="inline-flex items-center gap-1 text-xs font-semibold text-skutopia-600 hover:text-skutopia-700 disabled:opacity-60"
              >
                <LocateFixed className={cn("h-3.5 w-3.5", detectingCountry && "animate-pulse")} />
                {detectingCountry ? "Detecting…" : "Use my location"}
              </button>
            </div>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => {
                liveCountryRef.current = null;
                handleChange("country", e.target.value);
                setCountrySource("profile");
              }}
              placeholder="Detecting your country…"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            />
            {countrySource === "live" && formData.country ? (
              <p className="mt-1.5 text-xs text-[#656d76]">From your current location</p>
            ) : null}
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Website</Label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Official Email / Admissions</Label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="admissions@institution.edu"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Official Phone Number</Label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+260 97 000 0000"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30"
            />
          </div>
          {[
            { label: "Mission", placeholder: "Describe your organization's purpose and impact…", key: 'mission' },
            { label: "History", placeholder: "Share your story and milestones…", key: 'history' },
          ].map((field) => (
            <div key={field.label}>
              <Label className="text-sm font-medium mb-2 block">{field.label}</Label>
              <textarea
                value={formData[field.key as keyof typeof formData]}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 resize-none"
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-sm font-medium mb-2 block">
              Locations / campuses <span className="text-rose-500">*</span>
            </Label>
            <textarea
              value={formData.locations}
              onChange={(e) => handleChange("locations", e.target.value)}
              placeholder="Countries or campuses, comma separated (required)…"
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-sm focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 resize-none"
            />
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
          <div>
            {saveStatus === 'success' && (
              <p className="text-sm text-green-600">Profile updated successfully!</p>
            )}
            {saveStatus === 'error' && (
              <p className="text-sm text-red-600">Failed to update profile. Please try again.</p>
            )}
          </div>
          <Button 
            onClick={handleSave}
            disabled={loading}
            className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-800">
          <h4 className="font-medium mb-4">Impact Metrics</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { icon: Users, label: "Learners supported", value: impactMetrics.learnersSupported },
              { icon: Globe, label: "Countries reached", value: impactMetrics.countriesReached },
              { icon: Bookmark, label: "Programs published", value: impactMetrics.opportunitiesPublished },
              { icon: Calendar, label: "Years partnering", value: impactMetrics.yearsPartnering },
              { icon: Star, label: "Student followers", value: impactMetrics.studentFollowers },
            ].map((m) => (
              <div key={m.label} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 text-center">
                <m.icon className="w-5 h-5 text-skutopia-600 mx-auto mb-2" />
                <p className="text-lg font-semibold">{m.value}</p>
                <p className="text-xs text-gray-500">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </PortalCard>

      <PartnerFollowersPanel followers={followers} />
    </div>
  );
};

/* ─── TEAM ─── */
export const PartnerTeamView = () => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <p className="text-sm text-gray-500">
        Invite colleagues to help manage programs and review applications.
      </p>
      <Button className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl gap-2">
        <Plus className="w-4 h-4" />
        Invite Member
      </Button>
    </div>

    <PortalCard className="p-6">
      <h3 className="font-semibold mb-4">Current Team</h3>
      <div className="space-y-3">
        {[
          { name: "You", email: "admin@institution.com", role: "Owner" },
          { name: "James Kamau", email: "james@institution.com", role: "Recruiter" },
        ].map((member) => (
          <div
            key={member.email}
            className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-skutopia-100 dark:bg-skutopia-900/50 flex items-center justify-center text-skutopia-700 font-medium text-sm">
                {member.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium">{member.name}</p>
                <p className="text-xs text-gray-500">{member.email}</p>
              </div>
            </div>
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
              {member.role}
            </span>
          </div>
        ))}
      </div>
    </PortalCard>
  </div>
);

/* ─── BILLING ─── */
export const PartnerBillingView = () => {
  const { subscription, subscriptionPlans } = usePartnerData();
  
  // Calculate days remaining from subscription
  const daysRemaining = subscription?.end_date || subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.end_date || subscription.trial_end_date || '').getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : 30;
  
  const currentPlan = subscription?.plan || subscriptionPlans?.find(p => p.name === 'Free Trial');
  const proPlusPlan = subscriptionPlans?.find(p => p.name === 'Pro+');
  const freeFeatures = planFeatureList(currentPlan?.features, FREE_TRIAL_FEATURES);
  const proFeatures = planFeatureList(proPlusPlan?.features, PRO_PLUS_FEATURES);

  return (
    <div className="space-y-8">
      {/* Hero Section - Campaign Status */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold">Advertising Overview</h2>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {subscription?.status === 'trial' ? 'Active Trial' : 'Active'}
          </span>
          <span className="text-sm text-gray-600">{daysRemaining} days remaining</span>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Plan Card */}
        <PortalCard className="p-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Current Plan</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{currentPlan?.name || 'FREE TRIAL'}</h3>
              <p className="text-sm text-gray-500">{currentPlan?.description || 'Perfect for getting started with Skutopia.'}</p>
            </div>
            
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Price</span>
                <span className="text-lg font-semibold">${currentPlan?.price_monthly || 0}/month</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Status</span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                  {subscription?.status === 'trial' ? 'Active' : subscription?.status || 'Active'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Expires</span>
                <span className="text-sm font-medium">
                  {subscription?.end_date || subscription?.trial_end_date 
                    ? new Date(subscription.end_date || subscription.trial_end_date || '').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '16 Aug 2026'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Days Remaining</span>
                <span className="text-sm font-medium text-skutopia-600">{daysRemaining} Days</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Includes</p>
              {freeFeatures.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </PortalCard>

        {/* Upgrade Card */}
        <PortalCard className="p-6 bg-gradient-to-br from-skutopia-50 to-skutopia-100 dark:from-skutopia-950/30 dark:to-skutopia-900/30">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Upgrade to Pro+</p>
              <h3 className="text-2xl font-bold text-skutopia-600">${proPlusPlan?.price_monthly || 30}/month</h3>
              <p className="text-sm text-gray-600">Reach up to 5x more students</p>
            </div>
            
            <div className="pt-4 border-t border-skutopia-200/50 space-y-2">
              {proFeatures.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-skutopia-600 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button 
              onClick={() => {
                // Load Razorpay script dynamically and open modal
                const script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.async = true;
                script.onload = () => {
                  const options = {
                    key: 'rzp_test_TDtgxap0j2sRKH',
                    amount: 3000,
                    currency: 'INR',
                    name: 'Skutopia',
                    description: 'Pro+ Subscription',
                    theme: { color: '#044d35' }
                  };
                  const rzp = new (window as any).Razorpay(options);
                  rzp.open();
                };
                document.body.appendChild(script);
              }}
              className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl"
            >
              Upgrade
            </Button>
          </div>
        </PortalCard>
      </div>
    </div>
  );
};

/* ─── MANAGE CAMPAIGN ─── */
export const PartnerManageCampaignView = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Manage Campaign</h2>
          <p className="text-sm text-gray-500">Configure your advertising settings and preferences</p>
        </div>
        <Link to="/partners/billing">
          <Button variant="outline" className="rounded-xl">
            Back to Billing
          </Button>
        </Link>
      </div>

      <PortalCard className="p-6">
        <h3 className="font-semibold mb-4">Campaign Settings</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Campaign Name</Label>
            <Input placeholder="Enter campaign name" className="rounded-xl" />
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-2 block">Target Regions</Label>
            <Input placeholder="e.g., East Africa, West Africa" className="rounded-xl" />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Daily Budget</Label>
            <Input placeholder="Enter daily budget" className="rounded-xl" />
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
            <Button className="bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl">
              Save Changes
            </Button>
          </div>
        </div>
      </PortalCard>
    </div>
  );
};

/* ─── UPGRADE ─── */
export const PartnerUpgradeView = () => {
  const { user } = usePartnerData();
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    setLoading(true);

    // Check if Razorpay is already loaded
    if ((window as any).Razorpay) {
      openRazorpayModal();
      return;
    }

    // Load Razorpay script dynamically
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      openRazorpayModal();
    };

    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      alert('Failed to load payment gateway. Please check your internet connection and try again.');
      setLoading(false);
    };

    document.body.appendChild(script);
  };

  const openRazorpayModal = () => {
    try {
      const options = {
        key: 'rzp_test_TDtgxap0j2sRKH',
        amount: 3000, // $30 in cents (3000 paise)
        currency: 'INR',
        name: 'Skutopia',
        description: 'Pro+ Subscription',
        image: 'https://example.com/your-logo.png',
        handler: function (response: any) {
          // Payment successful
          console.log('Payment successful:', response);
          alert('Payment successful! Your Pro+ subscription is now active.');
          // TODO: Update user's subscription status in database
          setLoading(false);
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: ''
        },
        theme: {
          color: '#044d35'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            setLoading(false);
          }
        }
      };

      console.log('Opening Razorpay modal with options:', options);
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error opening Razorpay modal:', error);
      alert('Error opening payment modal. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Upgrade to Pro+</h2>
          <p className="text-sm text-gray-500">Unlock premium features and reach more students</p>
        </div>
        <Link to="/partners/billing">
          <Button variant="outline" className="rounded-xl">
            Back to Billing
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free Plan */}
        <PortalCard className="p-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Current Plan</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">FREE TRIAL</h3>
              <p className="text-sm text-gray-500">$0/month</p>
            </div>
            
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
              {FREE_TRIAL_FEATURES.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-gray-400" />
                  <span>{feature}</span>
              </div>
              ))}
            </div>
          </div>
        </PortalCard>

        {/* Pro+ Plan */}
        <PortalCard className="p-6 bg-gradient-to-br from-skutopia-50 to-skutopia-100 dark:from-skutopia-950/30 dark:to-skutopia-900/30 border-2 border-skutopia-200 dark:border-skutopia-800">
          <div className="space-y-4">
            <div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-skutopia-100 text-skutopia-700 text-xs font-medium mb-2">
                RECOMMENDED
              </span>
              <p className="text-sm font-medium text-gray-500 mb-1">Upgrade to Pro+</p>
              <h3 className="text-2xl font-bold text-skutopia-600">$30/month</h3>
              <p className="text-sm text-gray-600">Reach up to 5x more students</p>
            </div>
            
            <div className="pt-4 border-t border-skutopia-200/50 space-y-2">
              {PRO_PLUS_FEATURES.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-skutopia-600 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button 
              className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white rounded-xl"
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Loading payment gateway...
                </span>
              ) : (
                'Upgrade Now'
              )}
            </Button>
          </div>
        </PortalCard>
      </div>
    </div>
  );
};

/* ─── POSTS & TIMELINE ─── */
type ThreadedComment = {
  comment: PostReply;
  replies: PostReply[];
};

function buildThreadedComments(flatList: PostReply[]): ThreadedComment[] {
  if (!flatList || flatList.length === 0) return [];

  const commentMap = new Map<string, PostReply>();
  const childrenMap = new Map<string, PostReply[]>();
  const topLevel: PostReply[] = [];

  flatList.forEach((item) => {
    commentMap.set(item.id, item);
  });

  flatList.forEach((item) => {
    if (item.parent_reply_id && commentMap.has(item.parent_reply_id) && item.parent_reply_id !== item.id) {
      const existing = childrenMap.get(item.parent_reply_id) || [];
      existing.push(item);
      childrenMap.set(item.parent_reply_id, existing);
    } else {
      topLevel.push(item);
    }
  });

  return topLevel.map((c) => ({
    comment: c,
    replies: childrenMap.get(c.id) || [],
  }));
}

export const PartnerPostsView = () => {
  const { user } = useAuth();
  const {
    posts,
    programs,
    profile,
    loading,
    isProPlus,
    maxPostListings,
    maxProgramListings,
    createPost,
    updatePost,
    togglePostPublish,
    deletePost,
    fetchPostReactors,
    fetchPostReplies,
    addPostReply,
    deletePostReply,
    toggleReplyCheer,
  } = usePartnerData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("all");

  const atPostLimit = !isProPlus && posts.length >= maxPostListings;

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<PartnerPost | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tagsList, setTagsList] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete dialog
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Liked By / Reactions Modal
  const [reactorsModalPost, setReactorsModalPost] = useState<PartnerPost | null>(null);
  const [reactorsList, setReactorsList] = useState<PostReactor[]>([]);
  const [loadingReactors, setLoadingReactors] = useState(false);

  // Comments / Discussion Modal
  const [commentsModalPost, setCommentsModalPost] = useState<PartnerPost | null>(null);
  const [commentsList, setCommentsList] = useState<PostReply[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newCommentBody, setNewCommentBody] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [deletingReplyId, setDeletingReplyId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<PostReply | null>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);

  // Inline comments per post card (direct on-card reading & replying)
  const [expandedCommentsMap, setExpandedCommentsMap] = useState<Record<string, boolean>>({});
  const [inlineRepliesMap, setInlineRepliesMap] = useState<Record<string, PostReply[]>>({});
  const [loadingInlineComments, setLoadingInlineComments] = useState<Record<string, boolean>>({});
  const [inlineCommentInput, setInlineCommentInput] = useState<Record<string, string>>({});
  const [sendingInlineComment, setSendingInlineComment] = useState<Record<string, boolean>>({});
  const [inlineReplyingTo, setInlineReplyingTo] = useState<Record<string, PostReply | null>>({});

  const [reactingReplyId, setReactingReplyId] = useState<string | null>(null);

  const handleToggleReplyReaction = async (postId: string, reply: PostReply) => {
    if (reactingReplyId === reply.id) return;
    setReactingReplyId(reply.id);

    const updateList = (prevList: PostReply[]) =>
      (prevList || []).map((r) => {
        if (r.id === reply.id) {
          const nextCheered = !r.has_cheered;
          const diff = nextCheered ? 1 : -1;
          return {
            ...r,
            has_cheered: nextCheered,
            cheers_count: Math.max(0, (r.cheers_count || 0) + diff),
          };
        }
        return r;
      });

    setInlineRepliesMap((prev) => ({
      ...prev,
      [postId]: updateList(prev[postId] || []),
    }));
    setCommentsList((prev) => updateList(prev));

    try {
      await toggleReplyCheer(reply.id);
    } catch (err: any) {
      console.error("Error toggling reaction:", err);
      const revertList = (prevList: PostReply[]) =>
        (prevList || []).map((r) => (r.id === reply.id ? reply : r));
      setInlineRepliesMap((prev) => ({
        ...prev,
        [postId]: revertList(prev[postId] || []),
      }));
      setCommentsList((prev) => revertList(prev));
      toast.error("Could not update reaction");
    } finally {
      setReactingReplyId(null);
    }
  };

  const toggleInlineComments = async (postId: string) => {
    const nextState = !expandedCommentsMap[postId];
    setExpandedCommentsMap((prev) => ({ ...prev, [postId]: nextState }));
    if (nextState && !inlineRepliesMap[postId]) {
      setLoadingInlineComments((prev) => ({ ...prev, [postId]: true }));
      try {
        const list = await fetchPostReplies(postId);
        setInlineRepliesMap((prev) => ({ ...prev, [postId]: list }));
      } catch (e) {
        console.error("Error loading inline comments:", e);
      } finally {
        setLoadingInlineComments((prev) => ({ ...prev, [postId]: false }));
      }
    }
  };

  const handleSendInlineComment = async (postId: string, e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const body = (inlineCommentInput[postId] || "").trim();
    if (!body || sendingInlineComment[postId]) return;
    setSendingInlineComment((prev) => ({ ...prev, [postId]: true }));
    try {
      const replyTarget = inlineReplyingTo[postId];
      await addPostReply(postId, body, replyTarget?.id);
      const updatedList = await fetchPostReplies(postId);
      setInlineRepliesMap((prev) => ({ ...prev, [postId]: updatedList }));
      setInlineCommentInput((prev) => ({ ...prev, [postId]: "" }));
      setInlineReplyingTo((prev) => ({ ...prev, [postId]: null }));
      toast.success("Reply posted successfully!");
    } catch (err: any) {
      console.error("Error replying inline:", err);
      toast.error(err?.message || "Failed to post reply. Please try again.");
    } finally {
      setSendingInlineComment((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleStartInlineReplyTo = (postId: string, reply: PostReply, parentComment?: PostReply) => {
    const target = parentComment || reply;
    setInlineReplyingTo((prev) => ({ ...prev, [postId]: target }));
    const current = inlineCommentInput[postId] || "";
    const prefix = `@${reply.display_name} `;
    if (!current.startsWith(prefix)) {
      setInlineCommentInput((prev) => ({ ...prev, [postId]: prefix }));
    }
  };

  const handleDeleteInlineReply = async (postId: string, replyId: string) => {
    setDeletingReplyId(replyId);
    try {
      await deletePostReply(replyId);
      const updatedList = await fetchPostReplies(postId);
      setInlineRepliesMap((prev) => ({ ...prev, [postId]: updatedList }));
      toast.success("Comment deleted");
    } catch (err: any) {
      console.error("Error deleting reply:", err);
      toast.error(err?.message || "Failed to delete comment");
    } finally {
      setDeletingReplyId(null);
    }
  };

  // Auto-refresh comments modal in real-time when any learner replies
  useEffect(() => {
    if (!commentsModalPost?.id) return;
    const storyId = commentsModalPost.id;
    const channel = supabase
      .channel(`modal_story_replies_${storyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_replies",
          filter: `story_id=eq.${storyId}`,
        },
        async () => {
          try {
            const list = await fetchPostReplies(storyId);
            setCommentsList(list);
          } catch (e) {
            console.error("Live replies sync error:", e);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_reply_cheers",
        },
        async () => {
          try {
            const list = await fetchPostReplies(storyId);
            setCommentsList(list);
          } catch (e) {
            console.error("Live reply cheer sync error:", e);
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [commentsModalPost?.id, fetchPostReplies]);

  // Auto-refresh reactions modal in real-time when any learner likes
  useEffect(() => {
    if (!reactorsModalPost?.id) return;
    const storyId = reactorsModalPost.id;
    const channel = supabase
      .channel(`modal_story_cheers_${storyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_cheers",
          filter: `story_id=eq.${storyId}`,
        },
        async () => {
          try {
            const list = await fetchPostReactors(storyId);
            setReactorsList(list);
          } catch (e) {
            console.error("Live reactors sync error:", e);
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [reactorsModalPost?.id, fetchPostReactors]);

  // Open modal if URL has ?action=create
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      if (!isProPlus && posts.length >= maxPostListings) {
        toast.message("Listing limit reached", {
          description: `Free Trial includes up to 2 posts (5 listings total: 3 programs & 2 posts). Upgrade to Pro+ to publish unlimited posts.`,
        });
      } else {
        openCreateModal();
      }
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isProPlus, posts.length, maxPostListings]);

  const handleCreatePostClick = () => {
    if (atPostLimit) {
      toast.message("Upgrade to Pro+", {
        description: `Free Trial accounts include up to 2 posts and 3 programs (5 listings total). Please upgrade to Pro+ for unlimited listings!`,
      });
      return;
    }
    openCreateModal();
  };

  const openCreateModal = () => {
    setEditingPost(null);
    setTitle("");
    setBody("");
    setTagsList(["Admissions2026", "Scholarships"]);
    setCustomTagInput("");
    setImageUrl("");
    setImageFile(null);
    setImagePreview(null);
    setIsPublished(true);
    setIsModalOpen(true);
  };

  const openEditModal = (post: PartnerPost) => {
    setEditingPost(post);
    setTitle(post.title || "");
    setBody(post.body || "");
    setTagsList((post.tags || []).map((t) => t.replace(/^#+/, "")));
    setCustomTagInput("");
    setImageUrl(post.media_image_url || "");
    setImageFile(null);
    setImagePreview(post.media_image_url || null);
    setIsPublished(post.is_published ?? true);
    setIsModalOpen(true);
  };

  const handleAddTag = (rawTag: string) => {
    const clean = rawTag.trim().replace(/^#+/, "").replace(/,/g, "");
    if (!clean) return;
    if (!tagsList.some((t) => t.toLowerCase() === clean.toLowerCase())) {
      setTagsList((prev) => [...prev, clean]);
    }
    setCustomTagInput("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTagsList((prev) => prev.filter((t) => t !== tagToRemove));
  };

  const openReactorsModal = async (post: PartnerPost) => {
    setReactorsModalPost(post);
    setReactorsList(post.reactors_preview || []);
    setLoadingReactors(true);
    try {
      const list = await fetchPostReactors(post.id);
      setReactorsList(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReactors(false);
    }
  };

  const openCommentsModal = async (post: PartnerPost) => {
    setCommentsModalPost(post);
    setCommentsList([]);
    setNewCommentBody("");
    setReplyingTo(null);
    setLoadingComments(true);
    try {
      const list = await fetchPostReplies(post.id);
      setCommentsList(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleStartReplyTo = (reply: PostReply, parentComment?: PostReply) => {
    const target = parentComment || reply;
    setReplyingTo(target);
    const prefix = `@${reply.display_name} `;
    if (!newCommentBody.startsWith(prefix)) {
      setNewCommentBody(prefix);
    }
    setTimeout(() => {
      replyInputRef.current?.focus();
    }, 50);
  };

  const handleSendComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentsModalPost || !newCommentBody.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      await addPostReply(commentsModalPost.id, newCommentBody.trim(), replyingTo?.id);
      const updatedList = await fetchPostReplies(commentsModalPost.id);
      setCommentsList(updatedList);
      setNewCommentBody("");
      setReplyingTo(null);
      toast.success("Reply posted successfully!");
    } catch (err: any) {
      console.error("Error replying:", err);
      toast.error(err?.message || "Failed to post reply. Please try again.");
    } finally {
      setSendingComment(false);
    }
  };

  const handleTogglePublish = async (post: PartnerPost) => {
    const nextState = !post.is_published;
    try {
      await togglePostPublish(post.id, nextState);
      toast.success(nextState ? "Post is now Live on Feed!" : "Post moved to Drafts");
    } catch (err: any) {
      console.error("Error toggling publish state:", err);
      toast.error(err.message || "Failed to update publish status");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
        return;
      }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image file size must be less than 10MB");
        return;
      }
      
    setImageFile(file);
      const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) {
      toast.error("Please enter the content for your post");
      return;
    }

    setSaving(true);
    let finalImageUrl = imageUrl.trim();

    try {
      if (imageFile && user?.id) {
        setUploadingImage(true);
        const fileExt = imageFile.name.split(".").pop() || "jpg";
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        const filePath = `stories/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
          .from("student-story-images")
          .upload(filePath, imageFile, { upsert: true });

      if (uploadError) {
          // Fallback to program-images bucket if student-story-images hits permissions
          const { error: fallbackError } = await supabase.storage
            .from("program-images")
            .upload(`${user.id}/${fileName}`, imageFile, { upsert: true });

          if (fallbackError) {
        throw uploadError;
          } else {
            const { data: pubData } = supabase.storage
              .from("program-images")
              .getPublicUrl(`${user.id}/${fileName}`);
            finalImageUrl = pubData.publicUrl;
          }
        } else {
          const { data: pubData } = supabase.storage
            .from("student-story-images")
        .getPublicUrl(filePath);
          finalImageUrl = pubData.publicUrl;
        }
      }

      if (editingPost) {
        await updatePost(editingPost.id, {
          title: title.trim() || undefined,
          body: body.trim(),
          media_image_url: finalImageUrl || null,
          tags: tagsList,
          audience_type: "all",
          is_published: isPublished,
        });
        toast.success("Post updated successfully");
      } else {
        await createPost({
          title: title.trim() || undefined,
          body: body.trim(),
          media_image_url: finalImageUrl || null,
          tags: tagsList,
          audience_type: "all",
          is_published: isPublished,
        });
        toast.success("Post published to your timeline and Discover!");
      }

      setIsModalOpen(false);
    } catch (err: any) {
      console.error("Error saving post:", err);
      toast.error(err.message || "Failed to save post");
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deletePost(deleteId);
      toast.success("Post deleted successfully");
      setDeleteId(null);
    } catch (err: any) {
      console.error("Error deleting post:", err);
      toast.error(err.message || "Failed to delete post");
    } finally {
      setDeleting(false);
    }
  };

  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      if (statusFilter === "published" && !p.is_published) return false;
      if (statusFilter === "draft" && p.is_published) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = (p.title || "").toLowerCase().includes(q);
        const matchBody = (p.body || "").toLowerCase().includes(q);
        const matchTags = (p.tags || []).some((t) => t.toLowerCase().includes(q));
        if (!matchTitle && !matchBody && !matchTags) return false;
      }

      return true;
    });
  }, [posts, statusFilter, searchQuery]);

  const totalCheers = posts.reduce((sum, p) => sum + (p.cheers_count || 0), 0);
  const totalReplies = posts.reduce((sum, p) => sum + (p.reply_count || 0), 0);
  const publishedCount = posts.filter((p) => p.is_published).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-[#d0d7de] pb-5">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#656d76]">
            Campus Feed & Story Time
          </p>
          <h2 className="text-xl font-semibold tracking-tight text-[#1f2328] sm:text-2xl">
            Posts & Timeline
          </h2>
          <p className="mt-1 max-w-xl text-sm text-[#656d76]">
            Publish campus stories, application announcements, and updates directly to your institution feed and the mobile Discover screen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {atPostLimit ? (
            <Link to="/partners/billing">
              <Button
                type="button"
                className="inline-flex items-center gap-2 bg-[#00573C] hover:bg-[#004530] text-white font-semibold shadow-sm"
                onClick={() =>
                  toast.message("Upgrade to Pro+", {
                    description: "Free Trial includes up to 2 posts (5 listings total: 3 programs & 2 posts). Upgrade to Pro+ for unlimited listings.",
                  })
                }
              >
                <Plus className="w-4 h-4" />
                Create Post
              </Button>
            </Link>
          ) : (
            <Button
              onClick={handleCreatePostClick}
              className="inline-flex items-center gap-2 bg-[#00573C] hover:bg-[#004530] text-white font-semibold shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Create Post
            </Button>
          )}
        </div>
      </div>

      {/* Free Trial Limit Notice */}
      {!isProPlus && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-xs text-emerald-950">
          <div className="flex items-center gap-2.5">
            <span className="flex h-2 w-2 rounded-full bg-emerald-600" />
            <span>
              <strong>Free Trial Listings:</strong> {posts.length}/{maxPostListings} Posts · {programs.length}/{maxProgramListings} Programs used ({posts.length + programs.length}/5 maximum listings)
            </span>
          </div>
          <Link
            to="/partners/billing"
            className="font-bold text-[#00573C] underline hover:text-[#004530]"
          >
            Upgrade to Pro+ for Unlimited Listings →
          </Link>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <PortalCard className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-[#656d76]">
              Total Posts
            </span>
            <Newspaper className="h-4 w-4 text-[#00573C]" />
          </div>
          <div className="mt-2 text-2xl font-bold text-[#1f2328]">{posts.length}</div>
        </PortalCard>

        <PortalCard className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-[#656d76]">
              Live On Feed
            </span>
            <Sparkles className="h-4 w-4 text-[#00573C]" />
          </div>
          <div className="mt-2 text-2xl font-bold text-[#00573C]">{publishedCount}</div>
        </PortalCard>

        <PortalCard className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-[#656d76]">
              Likes / Cheers
            </span>
            <Heart className="h-4 w-4 text-rose-500 fill-rose-50" />
          </div>
          <div className="mt-2 text-2xl font-bold text-[#1f2328]">{totalCheers}</div>
        </PortalCard>

        <PortalCard className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-[#656d76]">
              Comments
            </span>
            <MessageCircle className="h-4 w-4 text-blue-500" />
          </div>
          <div className="mt-2 text-2xl font-bold text-[#1f2328]">{totalReplies}</div>
        </PortalCard>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStatusFilter("all")}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              statusFilter === "all"
                ? "bg-[#00573C] text-white"
                : "bg-white border border-[#d0d7de] text-[#1f2328] hover:bg-[#f6f8fa]"
            )}
          >
            All ({posts.length})
          </button>
          <button
            onClick={() => setStatusFilter("published")}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              statusFilter === "published"
                ? "bg-[#00573C] text-white"
                : "bg-white border border-[#d0d7de] text-[#1f2328] hover:bg-[#f6f8fa]"
            )}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => setStatusFilter("draft")}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              statusFilter === "draft"
                ? "bg-[#00573C] text-white"
                : "bg-white border border-[#d0d7de] text-[#1f2328] hover:bg-[#f6f8fa]"
            )}
          >
            Drafts ({posts.length - publishedCount})
          </button>
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#656d76]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search posts..."
            className="pl-9 h-9 text-xs"
          />
        </div>
      </div>

      {/* Post List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-7 w-7 rounded-full border-2 border-[#00573C] border-t-transparent animate-spin" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <PortalCard className="p-12 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#f6f8fa] flex items-center justify-center mb-3">
            <Newspaper className="w-6 h-6 text-[#656d76]" />
          </div>
          <h3 className="text-base font-semibold text-[#1f2328]">No posts found</h3>
          <p className="mt-1 text-sm text-[#656d76] max-w-sm mx-auto">
            {searchQuery
              ? "No posts match your search criteria."
              : "Share campus life, achievements, or deadlines to keep students updated."}
          </p>
          <Button
            onClick={openCreateModal}
            className="mt-4 bg-[#00573C] hover:bg-[#004530] text-white text-xs font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Create First Post
          </Button>
        </PortalCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredPosts.map((post) => (
            <PortalCard key={post.id} className="overflow-hidden flex flex-col justify-between border border-[#d0d7de] hover:shadow-md transition-shadow">
              <div>
                {/* Cover Image */}
                {post.media_image_url ? (
                  <div className="w-full h-48 bg-slate-100 relative overflow-hidden border-b border-[#d0d7de]">
                    <img
                      src={post.media_image_url}
                      alt={post.title || "Post media"}
                    className="w-full h-full object-cover"
                  />
                    <div className="absolute top-2.5 right-2.5">
                      <button
                        type="button"
                        onClick={() => handleTogglePublish(post)}
                        title="Click to toggle live status"
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-semibold shadow-md transition-transform active:scale-95 flex items-center gap-1.5 backdrop-blur-md",
                          post.is_published
                            ? "bg-emerald-600/90 text-white hover:bg-emerald-700"
                            : "bg-amber-600/90 text-white hover:bg-amber-700"
                        )}
                      >
                        <span className={cn("w-2 h-2 rounded-full", post.is_published ? "bg-white animate-pulse" : "bg-amber-200")} />
                        {post.is_published ? "Live on Feed" : "Draft"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 pb-0 flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleTogglePublish(post)}
                      title="Click to toggle live status"
                      className={cn(
                        "px-2.5 py-1 rounded-full text-xs font-semibold transition-transform active:scale-95 flex items-center gap-1.5",
                        post.is_published
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      )}
                    >
                      <span className={cn("w-2 h-2 rounded-full", post.is_published ? "bg-emerald-600" : "bg-amber-600")} />
                      {post.is_published ? "Live on Feed" : "Draft"}
                    </button>
                  </div>
                )}

                {/* Content */}
                <div className="p-5">
                  <div className="text-xs text-[#656d76] mb-1 font-medium">
                    {new Date(post.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  {post.title && (
                    <h3 className="text-base font-bold text-[#1f2328] mb-2 leading-snug break-words">
                      {post.title}
                    </h3>
                  )}
                  <p className="text-sm text-[#424a53] whitespace-pre-line leading-relaxed mb-3 break-words max-h-48 overflow-y-auto pr-1">
                    {post.body}
                  </p>

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {post.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-[#f6f8fa] text-[#00573C] text-xs font-medium rounded border border-[#d0d7de] hover:bg-emerald-50 transition-colors"
                        >
                          {tag.startsWith("#") ? tag : `#${tag}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Actions & Live Interactions */}
              <div className="px-5 py-3.5 bg-[#f6f8fa] border-t border-[#d0d7de] flex items-center justify-between gap-3">
                {/* Reactions & Comments with Facepile */}
                <div className="flex items-center gap-2.5 flex-wrap">
                  {/* Likes / Reactions button with Avatar Stack */}
                  <button
                    type="button"
                    onClick={() => openReactorsModal(post)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-[#d0d7de] hover:bg-rose-50/60 hover:border-rose-300 text-xs font-semibold text-[#1f2328] transition-all shadow-sm active:scale-95 cursor-pointer"
                    title="Click to see all students who liked this post"
                  >
                    <Heart className="w-4 h-4 text-rose-500 fill-rose-500 shrink-0" />
                    <span>
                      {post.cheers_count || 0} {post.cheers_count === 1 ? "Like" : "Likes"}
                    </span>
                    {post.reactors_preview && post.reactors_preview.length > 0 && (
                      <div className="flex items-center -space-x-1.5 ml-1">
                        {post.reactors_preview.slice(0, 3).map((reactor, i) => (
                          <div
                            key={reactor.user_id || i}
                            className="w-5 h-5 rounded-full ring-2 ring-white overflow-hidden bg-slate-200 shrink-0 shadow-sm"
                            title={reactor.display_name}
                          >
                            {reactor.profile_image_url ? (
                              <img
                                src={reactor.profile_image_url}
                                alt={reactor.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                              <div className="w-full h-full flex items-center justify-center text-[9px] font-bold text-slate-700 bg-emerald-100">
                                {reactor.display_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
                        ))}
            </div>
                    )}
                  </button>

                  {/* Comments button (toggles inline comment list & reply box) */}
                  <button
                  type="button"
                    onClick={() => toggleInlineComments(post.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-semibold transition-all shadow-sm active:scale-95 cursor-pointer",
                      expandedCommentsMap[post.id]
                        ? "bg-blue-50 border-blue-300 text-blue-800"
                        : "bg-white border-[#d0d7de] hover:bg-blue-50/60 hover:border-blue-300 text-[#1f2328]"
                    )}
                    title="Click to expand/collapse comments and reply"
                  >
                    <MessageCircle className="w-4 h-4 text-blue-500 shrink-0" />
                    <span>
                      {post.reply_count || 0} {post.reply_count === 1 ? "Comment" : "Comments"}
                    </span>
                    {expandedCommentsMap[post.id] ? (
                      <ChevronUp className="w-3.5 h-3.5 text-blue-600 ml-0.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 text-gray-500 ml-0.5" />
                    )}
                  </button>

                  {/* Button to open popup modal if desired */}
                  <button
                    type="button"
                    onClick={() => openCommentsModal(post)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Open in full discussion modal"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Edit & Delete Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                  variant="outline"
                    size="sm"
                    onClick={() => openEditModal(post)}
                    className="h-8 px-2.5 text-xs text-[#1f2328] hover:bg-white"
                  >
                    <Edit className="w-3.5 h-3.5 mr-1 text-[#656d76]" />
                    Edit
                </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(post.id)}
                    className="h-8 px-2.5 text-xs text-rose-600 hover:bg-rose-50 hover:border-rose-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Inline Comments & Direct Reply Section */}
              {expandedCommentsMap[post.id] && (
                <div className="border-t border-[#d0d7de] bg-[#f8fafc] p-4 space-y-3.5 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                      Discussion & Replies ({inlineRepliesMap[post.id]?.length ?? post.reply_count ?? 0})
                    </span>
                    <button
                    type="button"
                      onClick={() => toggleInlineComments(post.id)}
                      className="text-xs text-gray-500 hover:text-gray-800"
                    >
                      Hide
                    </button>
                  </div>

                  {/* Comments list */}
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                    {loadingInlineComments[post.id] ? (
                      <div className="py-6 flex justify-center">
                        <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : !inlineRepliesMap[post.id] || inlineRepliesMap[post.id].length === 0 ? (
                      <div className="py-4 text-center text-xs text-gray-500 bg-white rounded-lg border border-dashed border-gray-200 p-3">
                        No comments yet on this post. Write a reply below to start the conversation!
                      </div>
                    ) : (
                      buildThreadedComments(inlineRepliesMap[post.id]).map((thread) => (
                        <div key={thread.comment.id} className="space-y-2">
                          {/* Main Top-Level Comment */}
                          <div
                            className={cn(
                              "group p-3 rounded-lg border text-xs transition-colors",
                              thread.comment.is_partner
                                ? "bg-emerald-50/70 border-emerald-200 shadow-2xs"
                                : "bg-white border-[#d0d7de] shadow-2xs"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-200 ring-1 ring-black/5 shrink-0">
                                  {thread.comment.profile_image_url ? (
                                    <img
                                      src={thread.comment.profile_image_url}
                                      alt={thread.comment.display_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center font-bold text-[10px] text-emerald-800 bg-emerald-100">
                                      {thread.comment.display_name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-gray-900">
                                      {thread.comment.display_name}
                                    </span>
                                    {thread.comment.is_partner ? (
                                      <span className="text-[9px] bg-[#00573C] text-white px-1.5 py-0.2 rounded font-semibold">
                                        Institution
                                      </span>
                                    ) : thread.comment.grade ? (
                                      <span className="text-[9px] bg-slate-100 border border-slate-200 text-[#00573C] px-1.5 py-0.2 rounded font-semibold">
                                        Grade {thread.comment.grade}
                                      </span>
                                    ) : null}
                                  </div>
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(thread.comment.created_at).toLocaleDateString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleDeleteInlineReply(post.id, thread.comment.id)}
                                disabled={deletingReplyId === thread.comment.id}
                                title="Delete comment"
                                className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>

                            <p className="mt-1.5 text-gray-800 whitespace-pre-line leading-relaxed pl-9">
                              {thread.comment.body}
                            </p>

                            <div className="mt-1.5 pl-9 flex items-center gap-4">
                              <button
                                type="button"
                                onClick={() => handleToggleReplyReaction(post.id, thread.comment)}
                                disabled={reactingReplyId === thread.comment.id}
                                className={cn(
                                  "text-[11px] font-semibold flex items-center gap-1 transition-colors",
                                  thread.comment.has_cheered
                                    ? "text-rose-600 font-bold"
                                    : "text-gray-500 hover:text-rose-600"
                                )}
                              >
                                <Heart
                                  className={cn(
                                    "w-3.5 h-3.5 transition-transform active:scale-125",
                                    thread.comment.has_cheered ? "fill-rose-600 text-rose-600" : ""
                                  )}
                                />
                                <span>{thread.comment.has_cheered ? "Liked" : "Like"}</span>
                                {(thread.comment.cheers_count ?? 0) > 0 && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.2 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
                                    {thread.comment.cheers_count}
                                  </span>
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleStartInlineReplyTo(post.id, thread.comment, thread.comment)}
                                className="text-[11px] font-semibold text-[#00573C] hover:underline flex items-center gap-1"
                              >
                                <CornerDownRight className="w-3 h-3" />
                                Reply
                              </button>
                              {thread.replies.length > 0 && (
                                <span className="text-[10px] font-medium text-gray-400">
                                  {thread.replies.length} {thread.replies.length === 1 ? "reply" : "replies"}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Nested Replies matching this comment */}
                          {thread.replies.length > 0 && (
                            <div className="ml-5 sm:ml-8 pl-3 border-l-2 border-emerald-400 space-y-2">
                              {thread.replies.map((nested) => (
                                <div
                                  key={nested.id}
                                  className={cn(
                                    "group p-2.5 rounded-lg border text-xs transition-colors",
                                    nested.is_partner
                                      ? "bg-emerald-50/80 border-emerald-200 shadow-2xs"
                                      : "bg-white border-[#d0d7de] shadow-2xs"
                                  )}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full overflow-hidden bg-slate-200 ring-1 ring-black/5 shrink-0">
                                        {nested.profile_image_url ? (
                                          <img
                                            src={nested.profile_image_url}
                                            alt={nested.display_name}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center font-bold text-[9px] text-emerald-800 bg-emerald-100">
                                            {nested.display_name.charAt(0).toUpperCase()}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-bold text-gray-900 text-[11px]">
                                            {nested.display_name}
                                          </span>
                                          {nested.is_partner ? (
                                            <span className="text-[8.5px] bg-[#00573C] text-white px-1.2 py-0.2 rounded font-semibold">
                                              Institution
                                            </span>
                                          ) : nested.grade ? (
                                            <span className="text-[8.5px] bg-slate-100 border border-slate-200 text-[#00573C] px-1.2 py-0.2 rounded font-semibold">
                                              Grade {nested.grade}
                                            </span>
                                          ) : null}
                                        </div>
                                        <span className="text-[9.5px] text-gray-400">
                                          {new Date(nested.created_at).toLocaleDateString(undefined, {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </span>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => handleDeleteInlineReply(post.id, nested.id)}
                                      disabled={deletingReplyId === nested.id}
                                      title="Delete reply"
                                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>

                                  <p className="mt-1 text-gray-800 whitespace-pre-line leading-relaxed pl-8">
                                    {nested.body}
                                  </p>

                                  <div className="mt-1 pl-8 flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleReplyReaction(post.id, nested)}
                                      disabled={reactingReplyId === nested.id}
                                      className={cn(
                                        "text-[10.5px] font-semibold flex items-center gap-1 transition-colors",
                                        nested.has_cheered
                                          ? "text-rose-600 font-bold"
                                          : "text-gray-500 hover:text-rose-600"
                                      )}
                                    >
                                      <Heart
                                        className={cn(
                                          "w-3 h-3 transition-transform active:scale-125",
                                          nested.has_cheered ? "fill-rose-600 text-rose-600" : ""
                                        )}
                                      />
                                      <span>{nested.has_cheered ? "Liked" : "Like"}</span>
                                      {(nested.cheers_count ?? 0) > 0 && (
                                        <span className="text-[9.5px] font-bold px-1 py-0.2 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
                                          {nested.cheers_count}
                                        </span>
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleStartInlineReplyTo(post.id, nested, thread.comment)}
                                      className="text-[10.5px] font-semibold text-[#00573C] hover:underline flex items-center gap-1"
                                    >
                                      <CornerDownRight className="w-2.5 h-2.5" />
                                      Reply
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Inline Composer Form */}
                  <form
                    onSubmit={(e) => handleSendInlineComment(post.id, e)}
                    className="flex flex-col gap-1.5 bg-white p-2.5 rounded-lg border border-[#d0d7de] shadow-2xs"
                  >
                    {inlineReplyingTo[post.id] && (
                      <div className="flex items-center justify-between text-[11px] text-emerald-800 bg-emerald-50 px-2 py-1 rounded">
                        <span>
                          Replying to <span className="font-bold">{inlineReplyingTo[post.id]?.display_name}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setInlineReplyingTo((prev) => ({ ...prev, [post.id]: null }))}
                          className="font-bold text-emerald-700 hover:text-emerald-950"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-100 ring-1 ring-[#d0d7de] shrink-0">
                        {profile?.logo_url || profile?.avatar_url ? (
                          <img
                            src={profile.logo_url || profile.avatar_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-bold text-[10px] text-[#00573C] bg-emerald-100">
                            {(profile?.institution_name || "I").charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <Input
                        value={inlineCommentInput[post.id] || ""}
                        onChange={(e) =>
                          setInlineCommentInput((prev) => ({
                            ...prev,
                            [post.id]: e.target.value,
                          }))
                        }
                        placeholder={
                          inlineReplyingTo[post.id]
                            ? `Reply to @${inlineReplyingTo[post.id]?.display_name}...`
                            : `Write a reply as ${profile?.institution_name || "institution"}...`
                        }
                        className="text-xs h-8 flex-1"
                      />
                      <Button
                        type="submit"
                        disabled={!(inlineCommentInput[post.id] || "").trim() || sendingInlineComment[post.id]}
                        className="bg-[#00573C] hover:bg-[#004530] text-white text-xs h-8 px-3 shrink-0 gap-1"
                      >
                        {sendingInlineComment[post.id] ? (
                          <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3 h-3" />
                            <span>Reply</span>
                          </>
                        )}
                  </Button>
                    </div>
                  </form>
                </div>
              )}
            </PortalCard>
          ))}
        </div>
      )}

      {/* Liked By / Reactions Modal */}
      {reactorsModalPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-[#d0d7de] overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-5 py-4 border-b border-[#d0d7de] flex items-center justify-between bg-[#f6f8fa] shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4 text-rose-600 fill-rose-600" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-[#1f2328]">
                    Liked by ({reactorsList.length})
                  </h3>
                  <p className="text-xs text-gray-500 truncate max-w-xs">
                    {reactorsModalPost.title || reactorsModalPost.body.slice(0, 35)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setReactorsModalPost(null)}
                className="p-1.5 rounded-md text-[#656d76] hover:bg-white hover:text-[#1f2328] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 divide-y divide-gray-100">
              {loadingReactors ? (
                <div className="py-12 flex flex-col items-center justify-center gap-2">
                  <div className="h-6 w-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-gray-500">Loading reactions...</span>
                </div>
              ) : reactorsList.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">
                  <Heart className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  No likes yet on this post.
                </div>
              ) : (
                reactorsList.map((reactor, idx) => (
                  <div key={reactor.user_id || idx} className="flex items-center justify-between py-3 px-1">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 ring-2 ring-[#00573C]/10 shrink-0">
                        {reactor.profile_image_url ? (
                          <img
                            src={reactor.profile_image_url}
                            alt={reactor.display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-bold text-sm text-emerald-800 bg-emerald-100">
                            {reactor.display_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {reactor.display_name}
                          </p>
                          {(reactor as any).grade && (
                            <span className="text-[10px] bg-emerald-50 border border-emerald-200 text-[#00573C] px-1.5 py-0.2 rounded font-semibold">
                              Grade {(reactor as any).grade}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">Learner</p>
                      </div>
                    </div>
                    <span className="text-base shrink-0 ml-2" title="Liked">
                      {reactor.reaction || "❤️"}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="px-5 py-3 border-t border-[#d0d7de] bg-[#f6f8fa] flex justify-end shrink-0">
                  <Button
                    variant="outline"
                size="sm"
                onClick={() => setReactorsModalPost(null)}
                  >
                Close
                  </Button>
            </div>
          </div>
        </div>
      )}

      {/* Comments & Discussions Modal */}
      {commentsModalPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-[#d0d7de] overflow-hidden flex flex-col max-h-[85vh]">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-[#d0d7de] flex items-center justify-between bg-[#f6f8fa] shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-4 h-4 text-blue-600" />
              </div>
                <div>
                  <h3 className="font-bold text-base text-[#1f2328]">
                    Comments & Replies ({commentsList.length})
                  </h3>
                  <p className="text-xs text-gray-500 truncate max-w-xs">
                    {commentsModalPost.title || commentsModalPost.body.slice(0, 40)}
              </p>
            </div>
              </div>
              <button
                onClick={() => {
                  setCommentsModalPost(null);
                  setReplyingTo(null);
                }}
                className="p-1.5 rounded-md text-[#656d76] hover:bg-white hover:text-[#1f2328] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
          </div>

            {/* Comment List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3.5">
              {loadingComments ? (
                <div className="py-12 flex flex-col items-center justify-center gap-2">
                  <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-gray-500">Loading comments...</span>
            </div>
              ) : commentsList.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">
                  <MessageCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  No comments yet on this post. Write a reply below to start the conversation!
                </div>
              ) : (
                buildThreadedComments(commentsList).map((thread) => (
                  <div key={thread.comment.id} className="space-y-2">
                    {/* Top Level Comment */}
                    <div
                      className={cn(
                        "group relative flex items-start gap-3 p-3.5 rounded-lg border transition-colors",
                        thread.comment.is_partner
                          ? "bg-emerald-50/50 border-emerald-200"
                          : "bg-[#f6f8fa] border-[#d0d7de]"
                      )}
                    >
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-200 ring-2 ring-black/5 shrink-0 mt-0.5">
                        {thread.comment.profile_image_url ? (
                          <img
                            src={thread.comment.profile_image_url}
                            alt={thread.comment.display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-bold text-xs text-emerald-800 bg-emerald-100">
                            {thread.comment.display_name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-bold text-gray-900">
                              {thread.comment.display_name}
                            </span>
                            {thread.comment.is_partner ? (
                              <span className="text-[10px] bg-[#00573C] text-white px-1.5 py-0.2 rounded font-semibold">
                                Institution
                              </span>
                            ) : thread.comment.grade ? (
                              <span className="text-[10px] bg-white border border-[#d0d7de] text-[#00573C] px-1.5 py-0.2 rounded font-semibold">
                                Grade {thread.comment.grade}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {new Date(thread.comment.created_at).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteReply(thread.comment.id)}
                              disabled={deletingReplyId === thread.comment.id}
                              title="Delete comment"
                              className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 mt-1 leading-relaxed whitespace-pre-line break-words max-h-40 overflow-y-auto pr-1">
                          {thread.comment.body}
                        </p>

                        {/* Reply & Reaction action buttons on each comment */}
                        <div className="mt-2 flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => handleToggleReplyReaction(commentsModalPost.id, thread.comment)}
                            disabled={reactingReplyId === thread.comment.id}
                            className={cn(
                              "text-[11px] font-semibold flex items-center gap-1 transition-colors",
                              thread.comment.has_cheered
                                ? "text-rose-600 font-bold"
                                : "text-gray-500 hover:text-rose-600"
                            )}
                          >
                            <Heart
                              className={cn(
                                "w-3.5 h-3.5 transition-transform active:scale-125",
                                thread.comment.has_cheered ? "fill-rose-600 text-rose-600" : ""
                              )}
                            />
                            <span>{thread.comment.has_cheered ? "Liked" : "Like"}</span>
                            {(thread.comment.cheers_count ?? 0) > 0 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.2 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
                                {thread.comment.cheers_count}
                              </span>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleStartReplyTo(thread.comment, thread.comment)}
                            className="text-[11px] font-semibold text-[#00573C] hover:underline flex items-center gap-1"
                          >
                            <CornerDownRight className="w-3 h-3" />
                            Reply
                          </button>
                          {thread.replies.length > 0 && (
                            <span className="text-[10px] font-medium text-gray-400">
                              {thread.replies.length} {thread.replies.length === 1 ? "reply" : "replies"}
                            </span>
                          )}
          </div>
          </div>
                    </div>

                    {/* Nested Replies matching this comment */}
                    {thread.replies.length > 0 && (
                      <div className="ml-6 sm:ml-9 pl-3.5 border-l-2 border-emerald-400 space-y-2">
                        {thread.replies.map((nested) => (
                          <div
                            key={nested.id}
                            className={cn(
                              "group relative flex items-start gap-2.5 p-3 rounded-lg border transition-colors",
                              nested.is_partner
                                ? "bg-emerald-50/80 border-emerald-200"
                                : "bg-white border-[#d0d7de]"
                            )}
                          >
                            <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-200 ring-1 ring-black/5 shrink-0 mt-0.5">
                              {nested.profile_image_url ? (
                                <img
                                  src={nested.profile_image_url}
                                  alt={nested.display_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center font-bold text-[10px] text-emerald-800 bg-emerald-100">
                                  {nested.display_name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[11px] font-bold text-gray-900">
                                    {nested.display_name}
            </span>
                                  {nested.is_partner ? (
                                    <span className="text-[8.5px] bg-[#00573C] text-white px-1.2 py-0.2 rounded font-semibold">
                                      Institution
                                    </span>
                                  ) : nested.grade ? (
                                    <span className="text-[8.5px] bg-white border border-[#d0d7de] text-[#00573C] px-1.2 py-0.2 rounded font-semibold">
                                      Grade {nested.grade}
                                    </span>
                                  ) : null}
          </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9.5px] text-gray-400 shrink-0">
                                    {new Date(nested.created_at).toLocaleDateString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteReply(nested.id)}
                                    disabled={deletingReplyId === nested.id}
                                    title="Delete reply"
                                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
        </div>
                              </div>
                              <p className="text-xs text-gray-700 mt-1 leading-relaxed whitespace-pre-line break-words max-h-40 overflow-y-auto pr-1">
                                {nested.body}
                              </p>

                              <div className="mt-1.5 flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => handleToggleReplyReaction(commentsModalPost.id, nested)}
                                  disabled={reactingReplyId === nested.id}
                                  className={cn(
                                    "text-[10.5px] font-semibold flex items-center gap-1 transition-colors",
                                    nested.has_cheered
                                      ? "text-rose-600 font-bold"
                                      : "text-gray-500 hover:text-rose-600"
                                  )}
                                >
                                  <Heart
                                    className={cn(
                                      "w-3 h-3 transition-transform active:scale-125",
                                      nested.has_cheered ? "fill-rose-600 text-rose-600" : ""
                                    )}
                                  />
                                  <span>{nested.has_cheered ? "Liked" : "Like"}</span>
                                  {(nested.cheers_count ?? 0) > 0 && (
                                    <span className="text-[9.5px] font-bold px-1 py-0.2 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
                                      {nested.cheers_count}
                                    </span>
                                  )}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleStartReplyTo(nested, thread.comment)}
                                  className="text-[10.5px] font-semibold text-[#00573C] hover:underline flex items-center gap-1"
                                >
                                  <CornerDownRight className="w-2.5 h-2.5" />
                                  Reply
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Reply Context Banner */}
            {replyingTo && (
              <div className="px-4 py-2 bg-emerald-50 border-t border-emerald-200 flex items-center justify-between text-xs text-emerald-900 shrink-0">
                <span>
                  Replying to <span className="font-bold">{replyingTo.display_name}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setReplyingTo(null)}
                  className="text-xs text-emerald-700 hover:text-emerald-950 font-semibold"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Reply Composer Form */}
            <form onSubmit={handleSendComment} className="p-3.5 border-t border-[#d0d7de] bg-white flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 ring-1 ring-[#d0d7de] shrink-0">
                {profile?.logo_url || profile?.avatar_url ? (
                  <img
                    src={profile.logo_url || profile.avatar_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-bold text-xs text-[#00573C] bg-emerald-100">
                    {(profile?.institution_name || "I").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <Input
                ref={replyInputRef}
                value={newCommentBody}
                onChange={(e) => setNewCommentBody(e.target.value)}
                placeholder={
                  replyingTo
                    ? `Reply to @${replyingTo.display_name}...`
                    : `Write a reply as ${profile?.institution_name || "institution"}...`
                }
                className="text-xs h-9 flex-1"
                autoFocus={false}
              />
              <Button
                type="submit"
                disabled={!newCommentBody.trim() || sendingComment}
                className="bg-[#00573C] hover:bg-[#004530] text-white text-xs h-9 px-3.5 shrink-0 gap-1.5"
              >
                {sendingComment ? (
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Reply</span>
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Create / Edit Post Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-[#d0d7de] my-8 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[#d0d7de] flex items-center justify-between bg-[#f6f8fa]">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-[#00573C]" />
                <h3 className="font-semibold text-lg text-[#1f2328]">
                  {editingPost ? "Edit Timeline Post" : "Create Campus Post"}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-md text-[#656d76] hover:bg-white hover:text-[#1f2328]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Target Student Level — unified Discover Opportunities feed */}
          <div>
                <Label className="text-xs font-semibold text-[#1f2328]">
                  Discover audience
                </Label>
                <div className="mt-1.5 rounded-lg border border-[#d0d7de] bg-[#f6f8fa] px-3 py-2.5">
                  <p className="text-xs font-semibold text-[#00573C]">
                    All students
                  </p>
                  <p className="text-[11px] text-[#656d76] mt-0.5">
                    Posts appear in the unified Opportunities feed on Discover
                    (High school and Post High School combined).
                  </p>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="post-title" className="text-xs font-semibold text-[#1f2328]">
                  Headline / Title (Optional)
                </Label>
            <Input
                  id="post-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Applications Are Now Open for Upcoming Cohort!"
                  className="mt-1"
            />
          </div>

              {/* Body */}
          <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="post-body" className="text-xs font-semibold text-[#1f2328]">
                    Story / Announcement Content <span className="text-rose-500">*</span>
                  </Label>
                  <span className="text-xs text-[#656d76]">{body.length} characters</span>
                </div>
                <textarea
                  id="post-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  required
                  placeholder="Write your story, campus update, or announcement here..."
                  className="w-full rounded-md border border-[#d0d7de] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00573C] focus:border-transparent resize-y"
                />
              </div>

              {/* Cover Image Upload & Preview */}
              <div>
                <Label className="text-xs font-semibold text-[#1f2328]">
                  Cover Image
                </Label>
                <div className="mt-1.5">
                  {imagePreview ? (
                    <div className="relative rounded-lg overflow-hidden border border-[#d0d7de] max-h-56 bg-slate-100 flex items-center justify-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-h-56 w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-white hover:bg-black transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-[#d0d7de] rounded-lg p-6 text-center cursor-pointer hover:border-[#00573C] hover:bg-[#f6f8fa] transition-colors"
                    >
                      <UploadCloud className="w-8 h-8 mx-auto text-[#656d76] mb-2" />
                      <p className="text-xs font-semibold text-[#1f2328]">
                        Click to upload photo
                      </p>
                      <p className="text-[11px] text-[#656d76] mt-0.5">
                        PNG, JPG, WebP up to 10MB
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Hashtags & Topics */}
              <div>
                <Label className="text-xs font-semibold text-[#1f2328]">
                  Hashtags & Topics
                </Label>
                <p className="text-[11px] text-[#656d76] mb-2">
                  Add tags to help learners discover your post when searching or tapping on hashtags.
                </p>

                {/* Tag Chips */}
                {tagsList.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2.5 p-2 bg-[#f6f8fa] border border-[#d0d7de] rounded-lg">
                    {tagsList.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-white text-[#00573C] text-xs font-semibold rounded-md border border-emerald-200 shadow-2xs"
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="p-0.5 rounded-full hover:bg-rose-100 hover:text-rose-600 transition-colors"
                          title="Remove tag"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Tag Input */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#656d76]">#</span>
            <Input
                      value={customTagInput}
                      onChange={(e) => setCustomTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag(customTagInput);
                        }
                      }}
                      placeholder="Type a custom tag and press Enter..."
                      className="pl-7 text-xs h-9"
            />
          </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleAddTag(customTagInput)}
                    disabled={!customTagInput.trim()}
                    className="h-9 px-3 text-xs shrink-0"
                  >
                    + Add Tag
                  </Button>
            </div>

                {/* Suggested Tag Pills */}
                <div className="mt-2.5">
                  <span className="text-[11px] font-medium text-[#656d76] block mb-1">
                    Popular topics:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Admissions2026",
                      "Scholarships",
                      "FinancialAid",
                      "Internships",
                      "Leadership",
                      "STEM",
                      "StudyAbroad",
                      "CampusLife",
                      "Workshops",
                      "Competitions",
                      "HighSchool",
                      "PostHighSchool",
                    ].map((suggested) => {
                      const isAdded = tagsList.some((t) => t.toLowerCase() === suggested.toLowerCase());
                      return (
                        <button
                          key={suggested}
                          type="button"
                          onClick={() => {
                            if (isAdded) {
                              handleRemoveTag(suggested);
                            } else {
                              handleAddTag(suggested);
                            }
                          }}
                          className={cn(
                            "px-2 py-0.5 text-xs font-medium rounded border transition-colors",
                            isAdded
                              ? "bg-emerald-100 text-[#00573C] border-emerald-300 font-semibold"
                              : "bg-[#f6f8fa] hover:bg-[#eaeef2] text-[#656d76] border-[#d0d7de]"
                          )}
                        >
                          {isAdded ? `✓ #${suggested}` : `+ #${suggested}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Publish toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="publish-toggle"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="h-4 w-4 rounded border-[#d0d7de] text-[#00573C] focus:ring-[#00573C]"
                />
                <Label htmlFor="publish-toggle" className="text-xs font-medium text-[#1f2328] cursor-pointer">
                  Publish immediately (visible to students on Timeline & Discover)
                </Label>
              </div>

              {/* Footer actions */}
              <div className="pt-4 border-t border-[#d0d7de] flex items-center justify-end gap-2.5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
          </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#00573C] hover:bg-[#004530] text-white font-semibold min-w-[120px]"
                >
                  {saving || uploadingImage ? (
                    <span className="flex items-center gap-1.5">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </span>
                  ) : editingPost ? (
                    "Save Changes"
                  ) : (
                    "Publish Post"
                  )}
                </Button>
              </div>
        </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-[#d0d7de] p-6 space-y-4">
            <h3 className="text-lg font-bold text-[#1f2328]">Delete Post?</h3>
            <p className="text-sm text-[#656d76]">
              Are you sure you want to delete this post? This action will remove the story and all associated comments and reactions.
            </p>
            <div className="flex justify-end gap-2.5 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteId(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── COMING SOON ─── */
export const PartnerComingSoonView = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <PortalCard className="p-10 max-w-lg mx-auto">
    <div className="h-12 w-12 rounded-2xl bg-skutopia-50 flex items-center justify-center mb-5">
      <Briefcase className="w-6 h-6 text-skutopia-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 mb-4 leading-relaxed">{description}</p>
    <p className="text-xs text-gray-400">
      Coming soon. Designed to stay in sync with the student app.
    </p>
  </PortalCard>
);
